import { dimensionKeys, dimensionValues, type DimensionKey } from './dimensions';
import { logNormal, mulberry32, pickWeighted, poisson, gaussian } from './prng';
import { REFERENCE_DATE } from './reference';

/**
 * The corpus generator: one deterministic pass that produces every mention.
 *
 * The goal is not "random data" — it is data whose *shape responds correctly to
 * a filter*. Filtering to Negative has to make the outage dominate; filtering to
 * LinkedIn has to kill the video comments. That only happens if the dimensions
 * are drawn **conditionally on each other** rather than independently, which is
 * what the overlay tables below encode.
 */

/** One row of the corpus. */
export type MentionRow = {
  /**
   * `startOfDay(d).getTime()` — local midnight. Storing the day as a number
   * makes the date predicate an integer compare and lets the index be a plain
   * sorted array; storing a `Date` would make every comparison allocate.
   */
  day: number;
  brand: string;
  channel: string;
  intent: string;
  topic: string;
  conversation: string;
  sentiment: string;
  engagement: number;
  reach: number;
  npsScore: number;
  /**
   * Whether the mention was placed (paid or seeded) or arose on its own.
   *
   * A plain field rather than a `DimensionKey`: dimensions are the things the
   * filter chips control, and there is no Origin chip. Adding one to
   * `dimensionKeys` would make `assertRowsMatchFixtures` demand a fixture that
   * does not exist.
   */
  origin: Origin;
  /**
   * The media organisation that published the mention, or `null` when no
   * organisation did — which is most of the corpus.
   *
   * A plain field rather than a `DimensionKey`, for `origin`'s reason: there is
   * no Outlets chip, and adding one to `dimensionKeys` would make
   * `assertRowsMatchFixtures` demand a fixture that does not exist. It is a
   * `CategoryKey` in `pivot.ts` instead, which is the tier for "an ordered,
   * closed set of values with no chip".
   *
   * **Nullable, unlike every other field**, and that is the point. An outlet is
   * not a property every mention has: a reader's reply to a Facebook post has no
   * publisher, and stamping one on it would claim the newspaper wrote it. Only
   * the broadcast-shaped conversations can carry one — see `PRESS_CONVERSATIONS`.
   */
  outlet: string | null;
  /**
   * `engagement` split by interaction type. **The four always sum to
   * `engagement` exactly** — see `splitEngagement`. Without that guarantee a
   * by-type chart and the engagement KPI above it would disagree by a few
   * counts, which is precisely the kind of drift this corpus exists to avoid.
   */
  shares: number;
  comments: number;
  reactions: number;
  likes: number;
  /**
   * Local hour the mention was posted, `0`–`23`.
   *
   * `day` is local midnight and carries no time of day, so this is a second
   * field rather than a finer `day` — changing `day` to a full timestamp would
   * turn every date predicate in `rowIndex` from an integer compare into a
   * bucket-and-compare, and every existing figure with it.
   *
   * Drawn from `HOUR_SHAPE`, which is where the diurnal curve lives. Volume
   * only: `engagement` is drawn before this and is **not** re-scaled by the
   * hour, so a busy hour is busy because more mentions landed in it, not
   * because each one was worth more. Doubling that up would make the heatmap's
   * peak a product of two versions of the same claim.
   */
  hour: number;
  /**
   * The community — a group, forum or page — the mention was posted in, or
   * `null` when it was not posted in one.
   *
   * Nullable for `outlet`'s reason: most of the corpus is not group
   * conversation, and stamping a community on a direct message would claim a
   * private complaint was a public thread. Gated on the shape of the
   * conversation rather than sprayed at a rate — see `COMMUNITY_CONVERSATIONS`.
   */
  community: string | null;
  /**
   * The named individual account that posted the mention, or `null` — which is
   * almost all of it.
   *
   * The corpus is thousands of ordinary accounts and a handful of people with
   * an audience; this field names only the second kind. Mutually exclusive with
   * `outlet` by construction: a person is not a masthead, and a row carrying
   * both would be counted once as earned media and once as an individual voice.
   */
  voice: string | null;
  /**
   * The keyword the listening pipeline matched in the mention — the customer's
   * own word for what they were talking about.
   *
   * **Non-null on every row**, unlike `outlet`, `community` and `voice`. Those
   * three name things a mention may genuinely not have: most mentions have no
   * publisher and were not posted in a group. A mention always contains words,
   * and a nullable keyword would make the two keyword cards on Trends shares of
   * an unstated denominator — the same objection that keeps `ageBand` non-null.
   *
   * Drawn conditionally on `topic`, so the word and the category agree: a
   * mention filed under `network-coverage` says `নেটওয়ার্ক`, not `রোমিং`.
   * Modelling it as one keyword per row rather than a list is what keeps every
   * "the parts sum to the total" invariant in this file intact — see
   * `drawHashtag` for the same decision on the other side.
   */
  keyword: string;
  /**
   * The hashtag the mention carried, or `null` when it carried none — which is
   * most of the corpus.
   *
   * Nullable for `outlet`'s reason, and gated the same way: a tag is a thing
   * you put on a *public post*, so `HASHTAG_CONVERSATIONS` admits the broadcast
   * shapes and refuses the private ones. Stamping `#mygp` on a support ticket
   * would claim a helpdesk conversation was a campaign post.
   *
   * **One tag per row, not a list**, which is the one place this field is less
   * true to life than it could be — a real post carries two or three. A list
   * would make the `Top hashtags used` table's `% of overall` a share of tag
   * *instances* rather than of mentions, so its column would no longer be
   * comparable with any other figure in the app. Settled with the user before
   * it was built; recorded in `docs/rules.md`.
   */
  hashtag: string | null;
  /**
   * The author's age band and gender, as the product would infer them from a
   * public profile.
   *
   * Non-null on every row, unlike `outlet` and `community`: the pair is what
   * "audience" *means* here, and a nullable version would make every figure on
   * the demographics card a share of an unstated denominator. The cost is that
   * the corpus claims more certainty than a real inference pipeline has — noted
   * in `docs/rules.md` rather than modelled.
   */
  ageBand: string;
  /**
   * `male` or `female`, and only those two.
   *
   * A population pyramid is a two-sided chart, so the fixture carries the two
   * sides it can draw. Real demographic data is not binary and this is a
   * limitation of the fixture, not a claim about the audience — flagged for the
   * designer alongside the invented rosters.
   */
  gender: string;
  /**
   * The Bangladeshi administrative division the mention came from.
   *
   * Non-null, like the demographics: every mention is posted from somewhere.
   * Drawn in proportion to each division's subscriber base, then skewed for the
   * complaint family — which is what makes "complaints per 100K subscribers" a
   * ranking rather than a restatement of where the subscribers are.
   */
  division: string;
  /**
   * The tracked creator who posted the mention, or `null` — which is most of
   * the corpus.
   *
   * Nullable for `outlet`'s reason and gated the same way: the Influencers
   * module tracks a roster of thirty accounts, and everyone else in the corpus
   * is an ordinary person with no following to measure.
   *
   * **Mutually exclusive with both `outlet` and `voice`**, which is one more
   * exclusion than either of those carries. A masthead is not a creator, and a
   * row that was already attributed to a named individual by `voice` cannot be
   * attributed to a second named individual here — it would be counted once on
   * Community as a voice and once on Influencers as a creator, and the two
   * modules would disagree about who said it.
   *
   * `voice` and `influencer` are the same *idea* at two grains — six handles
   * the Community module ranks, thirty named creators this one analyses — and
   * they are two fields rather than one only because widening `VOICE_VALUES`
   * to thirty would redistribute its weights and rewrite every figure on a
   * module that is already built. Flagged for the designer in `docs/rules.md`.
   */
  influencer: string | null;
  /**
   * Which industry the creator's post falls in, or `null` when no creator
   * posted it.
   *
   * **Per row, not per creator**, and that is what the design asks for: the
   * `Industry specific split` table on the influencer-details frame
   * (964:16136) lists five industries for one creator, so a creator posts
   * across the field rather than sitting in one lane. Drawn with a bias
   * towards their own — see `INDUSTRY_PRIMARY_TILT`.
   *
   * Non-null exactly when `influencer` is. An industry with no creator behind
   * it is not a thing this module can navigate to.
   */
  industry: string | null;
  /**
   * How safe this single post is judged to be, or `null` when no creator
   * posted it.
   *
   * Drawn from the creator's own `safety` score so that the two agree by
   * construction: a creator scored 80 draws `safe` 80% of the time, which is
   * what lets the donut's ring and the figure in its middle be read together.
   * Over a narrow window they will disagree a little, and that is sampling
   * noise rather than a contradiction — the score is the creator's standing,
   * the ring is what they actually posted in the period on screen.
   */
  safetyBand: string | null;
};

/** A mention is either placed or it is not. */
export type Origin = 'organic' | 'seeded';

/** In fixture order, which is what pins each to a colour. See `CATEGORY_VALUES`. */
export const ORIGIN_VALUES = ['organic', 'seeded'] as const satisfies readonly Origin[];

/**
 * The media organisations tracked by the Media module, in ranked order.
 *
 * Bangladeshi outlets, because the corpus is Bangladeshi telcos — the same
 * reason `WEEKDAY` puts the weekend on Friday and Saturday. The order is the
 * fixture order every other category has: it pins each outlet to a colour and a
 * legend position, so a hard filter cannot recolour the page mid-session.
 *
 * `The Daily Star` leads the ramp because the Figma frame's own KPI reads
 * "Most stories / The Daily Star" (947:19009), and `OUTLET_MIX` weights it to.
 * Measured: 198 stories corpus-wide against Prothom Alo's 161, and 66 against
 * bdnews24's 52 once scoped to our own brand.
 *
 * **It does not therefore lead every window, and that is not a bug to fix.** On
 * the default 70 days the slice is 73 stories and bdnews24 comes out ahead 20
 * to 19 — a one-story gap, which is sampling noise at that n. The frame's KPI
 * values are example content rather than assertions about the data: its second
 * card reads "Worst day / 12 Mar", and no default window contains March at all
 * (the range is Jul 7 – Sep 14). Weighting the ranking is right; engineering a
 * particular window's winner would be fitting the corpus to a placeholder.
 */
export const OUTLET_VALUES = [
  'the-daily-star',
  'prothom-alo',
  'bdnews24',
  'the-business-standard',
  'dhaka-tribune',
  'somoy-tv',
  'jamuna-tv',
  'new-age',
] as const;

export type Outlet = (typeof OUTLET_VALUES)[number];

/**
 * Display names. Not derivable from the slug — `bdnews24.com` and `The Daily
 * Star` do not come out of a `replace`, and the mastheads are proper nouns with
 * their own capitalisation.
 *
 * **`Business Standard` drops the masthead's leading "The" on purpose.** A
 * rotated bar chart reserves `chartBarGeometry.categoryAxisWidth` — 128, which
 * mirrors `space/128` and is the widest step on the spacing scale — for its
 * category labels. `The Business Standard` measures 128px at `font-size/sm`, so
 * it wrapped onto two lines where the other seven labels sat on one. Measured
 * after: 102px, on one line, against the next-longest `bdnews24.com` at 84.
 *
 * Shortening the label rather than widening the gutter, because the gutter has
 * no wider token to take and a hand-picked 140 would be exactly the invented
 * component-specific value this repo forbids. These names are fixture content,
 * so choosing one that fits the existing scale is the cheaper of the two.
 */
export const OUTLET_LABELS: Readonly<Record<string, string>> = {
  'the-daily-star': 'The Daily Star',
  'prothom-alo': 'Prothom Alo',
  bdnews24: 'bdnews24.com',
  'the-business-standard': 'Business Standard',
  'dhaka-tribune': 'Dhaka Tribune',
  'somoy-tv': 'Somoy TV',
  'jamuna-tv': 'Jamuna TV',
  'new-age': 'New Age',
};

/* ------------------------------------------------------------------ *
 * Community rosters. See `MentionRow.community`, `.voice`, `.division`.
 *
 * **All of this is invented fixture content**, in the same standing as the
 * outlet roster above: nothing in Figma specifies the group names, the handles,
 * the member counts or the subscriber bases. The DeepDive v4 prototype supplied
 * the rosters and the Community frame's KPIs quote three of them back
 * (`GP Users BD`, `@gpcarehelp`, `Sylhet`), so the names are honoured and the
 * weights are set to reproduce those rankings. Every number here is open to the
 * designer's correction — see the open questions in `docs/rules.md`.
 * ------------------------------------------------------------------ */

/**
 * The monitored communities, ranked by the conversation they carry.
 *
 * Fixture order, like every other category: it pins each community to a colour
 * and a legend position so a filter cannot recolour the page mid-session.
 *
 * `Bangladesh Telecom Forum` is shortened to `BD Telecom Forum` for the reason
 * `The Business Standard` lost its "The": a rotated bar reserves
 * `chartBarGeometry.categoryAxisWidth` (128, the widest step on the spacing
 * scale) for its labels, and the full name does not fit on one line. Measured
 * rather than guessed — see the note on `OUTLET_LABELS`.
 */
export const COMMUNITY_VALUES = [
  'gp-users-bd',
  'bd-telecom-forum',
  'dhaka-tech-talk',
  'mobile-deals-bd',
  'student-sim-offers',
] as const;

export type Community = (typeof COMMUNITY_VALUES)[number];

export const COMMUNITY_LABELS: Readonly<Record<string, string>> = {
  'gp-users-bd': 'GP Users BD',
  'bd-telecom-forum': 'BD Telecom Forum',
  'dhaka-tech-talk': 'Dhaka Tech Talk',
  'mobile-deals-bd': 'Mobile Deals BD',
  'student-sim-offers': 'Student SIM Offers',
};

/**
 * Members per community, which is what lets volume be read per head.
 *
 * "GP Users BD carries the most conversation" and "Dhaka Tech Talk is the most
 * intense conversation" are different questions, and the second one cannot be
 * asked without this table — a 412,000-member group will out-mention a
 * 54,000-member one whatever is happening inside them.
 */
export const COMMUNITY_MEMBERS: Readonly<Record<string, number>> = {
  'gp-users-bd': 412_000,
  'bd-telecom-forum': 186_000,
  'dhaka-tech-talk': 54_000,
  'mobile-deals-bd': 121_000,
  'student-sim-offers': 38_000,
};

/**
 * The one community the brand runs itself. Everything else is earned ground.
 *
 * Carried as a set rather than a flag per entry so the common case — "is this
 * ours" — is one lookup, and so a second owned community is one string.
 */
export const OWNED_COMMUNITIES: ReadonlySet<string> = new Set(['gp-users-bd']);

/**
 * The named individual accounts with an audience, ranked by the reach they
 * carry. Everyone else in the corpus is an ordinary account with none.
 *
 * `@gpcarehelp` leads because the Community frame's own KPI reads
 * "Largest voice / @gpcarehelp · advocate" (951:20702), the same standing
 * `The Daily Star` has on the Media frame — the ranking is weighted for, the
 * particular window's winner is not engineered.
 */
export const VOICE_VALUES = [
  'gpcarehelp',
  'techbdreview',
  'dhakadeals',
  'simoffersbd',
  'rifatnetworks',
  'banglatechtalk',
] as const;

export type Voice = (typeof VOICE_VALUES)[number];

/** Handles, at-sign included — how the product would print them. */
export const VOICE_LABELS: Readonly<Record<string, string>> = {
  gpcarehelp: '@gpcarehelp',
  techbdreview: '@techbdreview',
  dhakadeals: '@dhakadeals',
  simoffersbd: '@simoffersbd',
  rifatnetworks: '@rifatnetworks',
  banglatechtalk: '@banglatechtalk',
};


/* ------------------------------------------------------------- influencers */

/**
 * The industries the Influencers module splits a creator's output by.
 *
 * Five, verbatim from the `Industry specific split` table on the
 * influencer-details frame (964:16136) — this is one of the few rosters in
 * this file the design actually names, so none of it is invented. Fixture
 * order is the drawn order, which pins each to a colour and a legend position
 * the way every other category list here does.
 */
export const INDUSTRY_VALUES = [
  'fashion-beauty',
  'technology',
  'food-beverage',
  'travel-lifestyle',
  'health-wellness',
] as const;

export type Industry = (typeof INDUSTRY_VALUES)[number];

/** Display names, spelled as the frame spells them — ampersands included. */
export const INDUSTRY_LABELS: Readonly<Record<string, string>> = {
  'fashion-beauty': 'Fashion & Beauty',
  technology: 'Technology',
  'food-beverage': 'Food & Beverage',
  'travel-lifestyle': 'Travel & Lifestyle',
  'health-wellness': 'Health & Wellness',
};

/**
 * How safe one post is judged to be, best to worst.
 *
 * Three bands rather than a continuous per-post score, because what the design
 * asks for is a donut: a ring needs slices, and a ring of four thousand
 * distinct scores is a solid disc. The band boundaries are the model — a post
 * is `safe`, or it wants a human to look at it, or it is the kind of thing a
 * brand does not want to be standing next to.
 */
export const SAFETY_BANDS = ['safe', 'needs-review', 'risky'] as const;

export type SafetyBand = (typeof SAFETY_BANDS)[number];

export const SAFETY_BAND_LABELS: Readonly<Record<string, string>> = {
  safe: 'Safe',
  'needs-review': 'Needs review',
  risky: 'Risky',
};

/** One tracked creator. Everything here is an attribute of the person, not of a post. */
export type InfluencerProfile = {
  id: string;
  name: string;
  /**
   * The channels they post on, as the corpus spells a channel. A row can only
   * be attributed to a creator whose list contains that row's own channel,
   * which is what keeps the `Platforms` column honest — a creator who is not
   * on TikTok never acquires a TikTok post.
   */
  platforms: readonly string[];
  followers: number;
  /** The industry most of their output falls in. See `INDUSTRY_PRIMARY_TILT`. */
  industry: Industry;
  /**
   * Figma's `Brand safety` column, 0–100.
   *
   * **Stored, not derived**, settled with the user (2026-09-17): it is a
   * standing judgement about the creator rather than a reading of one date
   * range, so it does not move when the date picker does. That is also what
   * lets the list table reproduce the frame's own column exactly.
   *
   * It is not inert, though — it is the weight `drawSafetyBand` draws each of
   * their posts against, so the per-period ring on the details page is
   * generated *from* this number rather than alongside it.
   */
  safety: number;
};

/**
 * The tracked creators, ranked by following.
 *
 * **The first ten are the frame's own** (964:7614) — name, platform marks,
 * follower count and brand-safety score transcribed verbatim, so the list's
 * first page reproduces the design exactly. The remaining four are invented
 * fixture content in the same standing as the outlet and community rosters,
 * and flagged for the designer alongside them.
 *
 * **One name is changed and it is deliberate.** The frame's second row reads
 * `John Doe`, sitting among nine plausible Bangladeshi names. That is
 * placeholder content in a list that otherwise reads as real — the same
 * judgement that dropped `#test` from the hashtag roster and `Mixed` from the
 * sentiments. It is `Tanvir Ahmed` here, keeping the row's figures (842K, 156
 * posts, 5.3%, 70) so nothing else about the frame stops reproducing.
 *

 * **Fourteen, and the number is a measurement rather than a taste.** The
 * frame's pagination draws five pages, but that is the `Pagination` component's
 * own default rather than a spec — no property on the instance says otherwise,
 * the same evidence that makes `Card title` a default. So the roster is sized
 * by what the corpus can actually feed it.
 *
 * The default 70-day window holds 1,004 rows. An outlet has already claimed 184
 * of them and a `voice` another 221, and a creator can be neither; of the 599
 * left, the shapes a creator can plausibly have posted come to about 410. Split
 * across *thirty* creators that is thirteen posts each — an industry table of
 * twos and ones and a donut with three slices totalling eight. Across fourteen
 * it is roughly fifty for the largest account and ten for the smallest, which
 * is a table that reads and a ring that has a shape. Fourteen also keeps the
 * pagination real: two pages of ten and four.
 *
 * This is the same trade `OUTLET_BY_CHANNEL` records one screen up — "one bar
 * of five rows and a trend nobody can read" is the failure being avoided, and
 * the honest note is that a 4,573-row *brand-mention* corpus was never going to
 * be thirty creators' full feeds. The frame's own `Posts analyzed` figures
 * (184, 156, 209) are an order of magnitude above anything this corpus can
 * produce for one creator, and are read as example content under the rule
 * `docs/rules.md` §1 already sets for KPI values.
 *
 * **Every creator is on Facebook, Instagram or TikTok and nowhere else**,
 * because those are the only three marks the frame draws. It is also what keeps
 * the ranking honest: an earlier pass put the one YouTube creator on a channel
 * no other creator shared, and because `drawInfluencer` picks among the
 * candidates *on that row's channel*, they took every eligible YouTube row and
 * came out top of a table sorted by a following twelve times smaller than the
 * leader's. A channel with a single candidate is a monopoly, not a weighting.
 *
 * **Brand safety does not track following, except in the ten the frame gives.**
 * Those ten descend 80 → 10 in lockstep with follower count, which reads as the
 * designer filling a column rather than a claim that big accounts are safe
 * ones. The four below them are spread independently, so the module's own
 * charts are not built on that accident.
 */
export const INFLUENCERS: readonly InfluencerProfile[] = [
  { id: 'ayesha-rahman', name: 'Ayesha Rahman', platforms: ['facebook', 'tiktok'], followers: 1_280_000, industry: 'fashion-beauty', safety: 80 },
  { id: 'tanvir-ahmed', name: 'Tanvir Ahmed', platforms: ['facebook'], followers: 842_000, industry: 'technology', safety: 70 },
  { id: 'nabila-hasan', name: 'Nabila Hasan', platforms: ['facebook', 'instagram'], followers: 715_000, industry: 'fashion-beauty', safety: 60 },
  { id: 'arif-chowdhury', name: 'Arif Chowdhury', platforms: ['instagram'], followers: 596_000, industry: 'food-beverage', safety: 60 },
  { id: 'maliha-karim', name: 'Maliha Karim', platforms: ['facebook', 'instagram'], followers: 482_000, industry: 'travel-lifestyle', safety: 30 },
  { id: 'samiul-islam', name: 'Samiul Islam', platforms: ['instagram', 'tiktok'], followers: 361_000, industry: 'technology', safety: 30 },
  { id: 'raisa-kabir', name: 'Raisa Kabir', platforms: ['facebook'], followers: 294_000, industry: 'health-wellness', safety: 30 },
  { id: 'farhan-noor', name: 'Farhan Noor', platforms: ['tiktok', 'instagram'], followers: 218_000, industry: 'food-beverage', safety: 20 },
  { id: 'tania-sultana', name: 'Tania Sultana', platforms: ['facebook'], followers: 147_000, industry: 'fashion-beauty', safety: 20 },
  { id: 'imran-hossain', name: 'Imran Hossain', platforms: ['facebook'], followers: 93_500, industry: 'technology', safety: 10 },
  { id: 'sadia-noor', name: 'Sadia Noor', platforms: ['instagram', 'tiktok'], followers: 88_400, industry: 'fashion-beauty', safety: 92 },
  { id: 'rakib-hasan', name: 'Rakib Hasan', platforms: ['facebook', 'tiktok'], followers: 81_200, industry: 'technology', safety: 74 },
  { id: 'mehjabin-alam', name: 'Mehjabin Alam', platforms: ['instagram'], followers: 76_900, industry: 'health-wellness', safety: 88 },
  { id: 'shuvo-barua', name: 'Shuvo Barua', platforms: ['tiktok'], followers: 70_300, industry: 'food-beverage', safety: 41 },
];

/** Ids in roster order — the fixture order every other category list here has. */
export const INFLUENCER_VALUES: readonly string[] = INFLUENCERS.map((i) => i.id);

/** Display names, so no table can draw a bare slug. */
export const INFLUENCER_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  INFLUENCERS.map((i) => [i.id, i.name]),
);

/** The roster keyed by id — the lookup every view does and none should re-derive. */
export const INFLUENCER_BY_ID: Readonly<Record<string, InfluencerProfile>> = Object.fromEntries(
  INFLUENCERS.map((i) => [i.id, i]),
);

/**
 * The divisions of Bangladesh the corpus draws from, and the subscriber base in
 * each — thousands of subscribers, so `18_400` is 18.4 million.
 *
 * Six of the country's eight, which is the prototype's own list. The bases are
 * what turns a complaint count into a rate: Dhaka will always file the most
 * complaints because Dhaka has the most subscribers, and the question the card
 * asks is where the network is failing *per head*.
 */
export const DIVISION_VALUES = [
  'dhaka',
  'chattogram',
  'sylhet',
  'khulna',
  'rajshahi',
  'rangpur',
] as const;

export type Division = (typeof DIVISION_VALUES)[number];

export const DIVISION_LABELS: Readonly<Record<string, string>> = {
  dhaka: 'Dhaka',
  chattogram: 'Chattogram',
  sylhet: 'Sylhet',
  khulna: 'Khulna',
  rajshahi: 'Rajshahi',
  rangpur: 'Rangpur',
};

/** Subscribers per division, in thousands. */
export const DIVISION_SUBSCRIBERS_K: Readonly<Record<string, number>> = {
  dhaka: 18_400,
  chattogram: 9_100,
  sylhet: 3_200,
  khulna: 4_800,
  rajshahi: 5_100,
  rangpur: 3_900,
};

/**
 * The age bands the audience is grouped into, youngest first.
 *
 * Order is the scale's own, not a ranking — a pyramid reads bottom-to-top by
 * age and shuffling these would make it meaningless. `sort: 'fixture'` is
 * therefore mandatory wherever this is the category.
 */
export const AGE_BANDS = ['18-24', '25-34', '35-44', '45-54', '55+'] as const;

export type AgeBand = (typeof AGE_BANDS)[number];

/** The two sides of the pyramid. See `MentionRow.gender`. */
export const GENDER_VALUES = ['male', 'female'] as const;

export type Gender = (typeof GENDER_VALUES)[number];

export const GENDER_LABELS: Readonly<Record<string, string>> = {
  male: 'Male',
  female: 'Female',
};

/**
 * The keywords the listening pipeline would have matched in a mention — the
 * actual words people type, in the script they type them in.
 *
 * **Bilingual on purpose**, which is the DeepDive v4 prototype's own decision
 * and the reason the Trends module carries keywords at all when it already has
 * a `topic` chip. A topic is the analyst's category (`Network coverage`); a
 * keyword is the customer's word for it (`নেটওয়ার্ক`). The two are not the same
 * list and a cloud of the first would not show you what the second says.
 *
 * **Fifty, since 2026-09-18.** The first fourteen came from the prototype's
 * twelve (`গ্রামীণফোন` dropped, since a brand name isn't a topic keyword and
 * would lead every cloud; `ডেটা`, `প্যাকেজ` and `বিল` added so every topic
 * family had a word). The other thirty-six were added for `Keyword mentions &
 * frequency`, which pages ten at a time and has a search box. Fourteen words
 * gave two pages and searches that found one bar. Settled with the user.
 *
 * The additions stay the kind of word a telco customer actually types: a
 * complaint phrase (`নেটওয়ার্ক নেই`, `টাকা কেটে নিয়েছে`), a product
 * (`ফ্লেক্সিলোড`, `বিকাশ`, `eSIM`), or a thing they need (`এনআইডি`,
 * `হেল্পলাইন`).
 *
 * Declaration order carries no ranking. Every keyword chart sorts by volume,
 * so the order only decides ties and fixture-sorted views, and the original
 * fourteen keep their places so neither of those moved.
 */
export const KEYWORD_VALUES = [
  /* The original fourteen, kept first and in their original order. */
  'network',
  'recharge',
  'offer',
  'internet',
  'data',
  'balance',
  'package',
  'sim',
  'five-g',
  'customer-care',
  'bill',
  'roaming',
  'mygp',
  'tower',
  /* Added 2026-09-18 for `Keyword mentions & frequency` — see the note above. */
  'no-network',
  'call-drop',
  'signal',
  'speed',
  'slow-internet',
  'four-g',
  'gb',
  'validity',
  'price',
  'expensive',
  'discount',
  'cashback',
  'bonus',
  'minutes',
  'sms',
  'flexiload',
  'bkash',
  'emergency-balance',
  'deduction',
  'refund',
  'helpline',
  'complaint',
  'esim',
  'sim-replacement',
  'nid',
  'biometric',
  'app-crash',
  'login',
  'update',
  'star',
  'points',
  'handset',
  'international-call',
  'center',
  'ad',
  'job',
] as const;

export type Keyword = (typeof KEYWORD_VALUES)[number];

/**
 * The word itself — Bangla where the word is Bangla, Latin where it is not.
 *
 * **Native script only, where the prototype's own stacked bar printed
 * `নেটওয়ার্ক (network)`.** A rotated bar chart reserves
 * `chartBarGeometry.categoryAxisWidth` — 128, the widest step on the spacing
 * scale — for its category labels, and the gloss does not fit inside it: the
 * bilingual form measures past 128px at `font-size/sm` and would wrap to two
 * lines on every row, which is the exact failure `OUTLET_LABELS` shortened
 * `The Business Standard` to avoid. §6 of the rules says fix the label, not the
 * gutter, and there is no wider token to widen it to.
 *
 * `5G`, `MyGP` are already Latin, so a gloss would have read `5G (five-g)`.
 */
export const KEYWORD_LABELS: Readonly<Record<string, string>> = {
  network: 'নেটওয়ার্ক',
  recharge: 'রিচার্জ',
  offer: 'অফার',
  internet: 'ইন্টারনেট',
  data: 'ডেটা',
  balance: 'ব্যালেন্স',
  package: 'প্যাকেজ',
  sim: 'সিম',
  'five-g': '5G',
  'customer-care': 'কাস্টমার কেয়ার',
  bill: 'বিল',
  roaming: 'রোমিং',
  mygp: 'MyGP',
  tower: 'টাওয়ার',
  'no-network': 'নেটওয়ার্ক নেই',
  'call-drop': 'কল ড্রপ',
  signal: 'সিগন্যাল',
  speed: 'স্পিড',
  'slow-internet': 'স্লো ইন্টারনেট',
  'four-g': '4G',
  gb: 'জিবি',
  validity: 'মেয়াদ',
  price: 'দাম',
  expensive: 'দামি',
  discount: 'ছাড়',
  cashback: 'ক্যাশব্যাক',
  bonus: 'বোনাস',
  minutes: 'মিনিট',
  sms: 'SMS',
  flexiload: 'ফ্লেক্সিলোড',
  bkash: 'বিকাশ',
  'emergency-balance': 'ইমার্জেন্সি ব্যালেন্স',
  deduction: 'টাকা কেটে নিয়েছে',
  refund: 'রিফান্ড',
  helpline: 'হেল্পলাইন',
  complaint: 'অভিযোগ',
  esim: 'eSIM',
  'sim-replacement': 'সিম রিপ্লেস',
  nid: 'এনআইডি',
  biometric: 'বায়োমেট্রিক',
  'app-crash': 'অ্যাপ ক্র্যাশ',
  login: 'লগইন',
  update: 'আপডেট',
  star: 'স্টার',
  points: 'পয়েন্ট',
  handset: 'হ্যান্ডসেট',
  'international-call': 'আন্তর্জাতিক কল',
  center: 'জিপি সেন্টার',
  ad: 'বিজ্ঞাপন',
  job: 'চাকরি',
};

/**
 * The English gloss, for the accessible name and the tooltip only.
 *
 * A reader who cannot read Bengali gets nothing at all from a cloud of Bangla
 * words, and a screen reader announcing `নেটওয়ার্ক` in an English-language
 * document announces nothing useful either. The gloss is what makes the mark
 * legible to both without putting it on the axis, where it does not fit.
 *
 * `null` where the label is already the English word — `5G (5G)` is noise.
 */
export const KEYWORD_GLOSS: Readonly<Record<string, string | null>> = {
  network: 'network',
  recharge: 'recharge',
  offer: 'offer',
  internet: 'internet',
  data: 'data',
  balance: 'balance',
  package: 'package',
  sim: 'SIM',
  'five-g': null,
  'customer-care': 'customer care',
  bill: 'bill',
  roaming: 'roaming',
  mygp: null,
  tower: 'tower',
  'no-network': 'no network',
  'call-drop': 'call drop',
  signal: 'signal',
  speed: 'speed',
  'slow-internet': 'slow internet',
  'four-g': null,
  gb: 'GB',
  validity: 'validity',
  price: 'price',
  expensive: 'expensive',
  discount: 'discount',
  cashback: 'cashback',
  bonus: 'bonus',
  minutes: 'minutes',
  sms: null,
  flexiload: 'Flexiload',
  bkash: 'bKash',
  'emergency-balance': 'emergency balance',
  deduction: 'money deducted',
  refund: 'refund',
  helpline: 'helpline',
  complaint: 'complaint',
  esim: null,
  'sim-replacement': 'SIM replacement',
  nid: 'NID',
  biometric: 'biometric',
  'app-crash': 'app crash',
  login: 'login',
  update: 'update',
  star: 'GP Star',
  points: 'points',
  handset: 'handset',
  'international-call': 'international call',
  center: 'GP Center',
  ad: 'advertisement',
  job: 'job',
};

/**
 * The hashtags the corpus carries, in the order the `Top hashtags used` table
 * ranks them on the default window.
 *
 * Eight of the nine the Figma table draws (951:26706). **`#test` is dropped**:
 * it is placeholder content rather than a tag anybody would post, sitting in a
 * list that is otherwise plausible — the same judgement that removed `Mixed`
 * from the sentiment fixture. `#network`, `#5g` and `#offer` are added so every
 * topic family has a tag, since a tag roster shorter than the topic roster
 * makes the tag column a near-duplicate of a handful of topics.
 */
export const HASHTAG_VALUES = [
  'services',
  'mygp',
  'packages',
  'mobile',
  'data',
  'community',
  'app',
  'network',
  'five-g',
  'offer',
] as const;

export type Hashtag = (typeof HASHTAG_VALUES)[number];

/** With the `#`, which is how a tag is written everywhere it is shown. */
export const HASHTAG_LABELS: Readonly<Record<string, string>> = {
  services: '#services',
  mygp: '#mygp',
  packages: '#packages',
  mobile: '#mobile',
  data: '#data',
  community: '#community',
  app: '#app',
  network: '#network',
  'five-g': '#5g',
  offer: '#offer',
};

/** The interaction types `engagement` decomposes into, most valuable first. */
export const ENGAGEMENT_TYPES = ['shares', 'comments', 'reactions', 'likes'] as const;

export type EngagementType = (typeof ENGAGEMENT_TYPES)[number];

/*
 * Note what is absent: no `mentions: 1`. "Mentions" is a *metric* whose reduce
 * is `rows.length`, not a column — a column would invite someone to sum it per
 * bucket and get a subtly different answer from the row count.
 */

/** How many days the corpus spans, ending on `REFERENCE_DATE`. */
export const CORPUS_DAYS = 400;

/** Mean mentions per day before seasonality, drift and spikes. ~10 × 400 ≈ 4,000 rows. */
const BASE_LAMBDA = 10;

/** Total volume growth across the whole span, applied linearly. */
const DRIFT = 0.25;

/**
 * Day-of-week multipliers, indexed by `Date.getDay()` (0 = Sunday).
 *
 * The weekend dip sits on **Friday and Saturday**, which is the Bangladeshi
 * working week, not the Saturday/Sunday a European fixture would assume. The
 * domain is Bangladeshi telcos, so getting this backwards would put the quiet
 * days in the wrong place on every weekly chart.
 */
const WEEKDAY = [1.02, 1.06, 1.04, 1.0, 0.98, 0.82, 0.86];

type Weights = Readonly<Record<string, number>>;

/**
 * A declared event: a window where volume multiplies and the dimension draws
 * lean a particular way.
 *
 * Declared as data, not as an `if` inside the loop, so the debug panel can list
 * them and a reader can see the whole story of the corpus in one place.
 */
export type Spike = {
  id: string;
  label: string;
  /** Days before `REFERENCE_DATE` that the window opens. */
  startsDaysAgo: number;
  days: number;
  /** Volume multiplier while the window is open. */
  multiplier: number;
  /** Which values the draws lean towards inside the window. */
  bias: Partial<Record<DimensionKey, Weights>>;
};

export const spikes: readonly Spike[] = [
  {
    id: 'outage',
    label: 'Robi network outage',
    startsDaysAgo: 26,
    days: 3,
    multiplier: 3.4,
    bias: {
      brand: { robi: 6 },
      topic: { 'network-coverage': 7, 'internet-speed': 3, 'call-quality': 3, promotions: 0.05 },
      intent: { 'outage-report': 8, complaint: 3, praise: 0.1 },
      sentiment: { negative: 5, positive: 0.1 },
    },
  },
  {
    id: 'campaign',
    label: 'Grameenphone campaign launch',
    startsDaysAgo: 63,
    days: 5,
    multiplier: 2.1,
    bias: {
      brand: { grameenphone: 5 },
      topic: { promotions: 6, 'device-offers': 2.5, 'data-packs': 2 },
      intent: { 'purchase-intent': 4, praise: 2.5, complaint: 0.4 },
      sentiment: { positive: 4, negative: 0.3 },
    },
  },
];

/* ------------------------------------------------------------------ *
 * Priors. A value absent from a table takes weight 1.
 * ------------------------------------------------------------------ */

const BASE: Readonly<Record<DimensionKey, Weights>> = {
  brand: { grameenphone: 38, banglalink: 20, robi: 24, airtel: 12, teletalk: 6 },
  channel: { facebook: 40, instagram: 14, x: 8, youtube: 16, tiktok: 14, linkedin: 4, reddit: 4 },
  intent: {
    complaint: 18,
    praise: 10,
    question: 16,
    'purchase-intent': 7,
    cancellation: 3,
    comparison: 5,
    recommendation: 6,
    'support-request': 12,
    'billing-issue': 8,
    'feature-request': 3,
    'outage-report': 5,
    'refund-request': 3,
    renewal: 2,
    upgrade: 2,
  },
  topic: {
    pricing: 12,
    'network-coverage': 12,
    'data-packs': 11,
    'customer-service': 9,
    roaming: 3,
    billing: 8,
    'app-experience': 6,
    'store-experience': 3,
    promotions: 7,
    'call-quality': 7,
    'internet-speed': 9,
    recharge: 6,
    'sim-activation': 4,
    'device-offers': 3,
    'loyalty-rewards': 3,
    'contract-terms': 2,
    advertising: 2,
    sponsorships: 2,
    sustainability: 1,
    careers: 1,
  },
  conversation: {
    'direct-message': 9,
    comment: 24,
    mention: 16,
    review: 6,
    'support-ticket': 6,
    reply: 12,
    'quote-post': 4,
    repost: 5,
    'story-mention': 3,
    'forum-thread': 3,
    'blog-comment': 2,
    'live-chat': 4,
    'app-store-review': 3,
    'video-comment': 8,
  },
  sentiment: { positive: 30, neutral: 40, negative: 25 },
};

/** channel | brand — who each operator's audience actually is. */
const CHANNEL_BY_BRAND: Readonly<Record<string, Weights>> = {
  grameenphone: { linkedin: 1.6, instagram: 1.2 },
  banglalink: { tiktok: 1.2 },
  robi: { tiktok: 1.3, youtube: 1.2 },
  airtel: { instagram: 1.3, tiktok: 1.2, linkedin: 0.6 },
  teletalk: { facebook: 1.3, tiktok: 0.6, linkedin: 0.5 },
};

/** intent | channel — what people come to each platform to say. */
const INTENT_BY_CHANNEL: Readonly<Record<string, Weights>> = {
  facebook: { 'support-request': 1.2, 'billing-issue': 1.2 },
  instagram: { praise: 1.3, 'purchase-intent': 1.2, 'billing-issue': 0.6 },
  x: { complaint: 1.6, 'outage-report': 1.8, praise: 0.7 },
  youtube: { question: 1.2, comparison: 1.3 },
  tiktok: { praise: 1.4, question: 0.8, 'billing-issue': 0.5 },
  linkedin: { praise: 1.6, recommendation: 1.5, complaint: 0.4, 'billing-issue': 0.3 },
  reddit: { comparison: 2.0, complaint: 1.3, praise: 0.5, 'support-request': 0.7 },
};

/** topic | intent — the strongest correlation in the corpus. */
const TOPIC_BY_INTENT: Readonly<Record<string, Weights>> = {
  'outage-report': {
    'network-coverage': 4,
    'internet-speed': 3,
    'call-quality': 3,
    pricing: 0.2,
    promotions: 0.1,
  },
  'billing-issue': { billing: 5, recharge: 2, pricing: 1.5, 'network-coverage': 0.2 },
  'refund-request': { billing: 4, recharge: 2, 'data-packs': 1.5 },
  'purchase-intent': { 'data-packs': 2, 'device-offers': 2.5, promotions: 1.8, pricing: 1.5 },
  praise: { 'network-coverage': 1.2, 'customer-service': 1.3, promotions: 1.2 },
  complaint: {
    'network-coverage': 1.6,
    'internet-speed': 1.5,
    'customer-service': 1.4,
    'call-quality': 1.4,
  },
  comparison: { pricing: 2.2, 'data-packs': 1.8, 'network-coverage': 1.4 },
  'support-request': { 'app-experience': 1.6, 'sim-activation': 1.8, recharge: 1.5 },
  'feature-request': { 'app-experience': 2.5, 'loyalty-rewards': 1.6 },
  cancellation: { 'contract-terms': 3, pricing: 1.8, billing: 1.5 },
  renewal: { 'data-packs': 2, 'contract-terms': 1.8 },
  upgrade: { 'device-offers': 2.2, 'data-packs': 1.8 },
  recommendation: { promotions: 1.5, 'data-packs': 1.4 },
  question: { 'data-packs': 1.4, recharge: 1.4, 'sim-activation': 1.3 },
};

/** conversation | channel — the structural one: YouTube has video comments, Reddit has threads. */
const CONVERSATION_BY_CHANNEL: Readonly<Record<string, Weights>> = {
  facebook: { comment: 1.3, 'direct-message': 1.3, 'live-chat': 1.4, 'video-comment': 0.3 },
  instagram: { 'story-mention': 3, comment: 1.2, 'video-comment': 1.2, 'forum-thread': 0.05 },
  x: { 'quote-post': 3, repost: 3, mention: 1.6, 'video-comment': 0.1, 'live-chat': 0.1 },
  youtube: {
    'video-comment': 5,
    comment: 1.2,
    'quote-post': 0.1,
    repost: 0.1,
    'story-mention': 0.05,
  },
  tiktok: { 'video-comment': 4, comment: 1.3, 'story-mention': 0.2, 'forum-thread': 0.05 },
  linkedin: { repost: 1.8, comment: 1.2, 'story-mention': 0.05, 'video-comment': 0.2 },
  reddit: {
    'forum-thread': 6,
    comment: 1.4,
    'direct-message': 0.3,
    'story-mention': 0.02,
    'video-comment': 0.05,
  },
};

/** sentiment | topic — the pain points skew negative, the marketing skews positive. */
const SENTIMENT_BY_TOPIC: Readonly<Record<string, Weights>> = {
  'network-coverage': { negative: 2.0, positive: 0.6 },
  'internet-speed': { negative: 1.8, positive: 0.6 },
  'call-quality': { negative: 1.7, positive: 0.6 },
  billing: { negative: 1.9, positive: 0.5 },
  'customer-service': { negative: 1.5, positive: 0.9 },
  'contract-terms': { negative: 1.6, positive: 0.6 },
  promotions: { positive: 1.8, negative: 0.5 },
  'loyalty-rewards': { positive: 1.7, negative: 0.6 },
  'device-offers': { positive: 1.3 },
  sponsorships: { positive: 1.5, negative: 0.6 },
  sustainability: { positive: 1.4, negative: 0.7 },
  careers: { positive: 1.3 },
};

/** sentiment | brand — the incumbent is liked a little more, the state operator less. */
const SENTIMENT_BY_BRAND: Readonly<Record<string, Weights>> = {
  grameenphone: { positive: 1.15 },
  teletalk: { negative: 1.4, positive: 0.7 },
};

/** Mean NPS by sentiment. `nps` is a mean, which is why it must never be summed. */
const NPS_BY_SENTIMENT: Readonly<Record<string, readonly [number, number]>> = {
  positive: [8.8, 1.0],
  neutral: [6.8, 1.2],
  negative: [3.0, 1.6],
};

/* ------------------------------------------------------------------ *
 * Drawing
 * ------------------------------------------------------------------ */

/**
 * Multiply the base prior by each overlay and draw.
 *
 * Overlays compose: a row inside the outage window that is already about
 * `network-coverage` gets both the topic bias and the spike bias, which is what
 * makes the spike read as an *outage* rather than as an undifferentiated bump.
 */
function draw(
  rand: () => number,
  key: DimensionKey,
  overlays: readonly (Weights | undefined)[],
): string {
  const values = dimensionValues[key];
  const base = BASE[key];
  const weights = values.map((value) => {
    let weight = base[value] ?? 1;
    for (const overlay of overlays) {
      if (overlay && value in overlay) weight *= overlay[value];
    }
    return weight;
  });
  return values[pickWeighted(rand, weights)];
}

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const addDays = (date: Date, days: number) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/** The first day of the corpus — `CORPUS_DAYS - 1` days before the reference. */
export const CORPUS_START = addDays(startOfDay(REFERENCE_DATE), -(CORPUS_DAYS - 1));

/** The window a spike occupies, as day-index bounds into the corpus. */
function spikeWindow(spike: Spike): { from: number; to: number } {
  const from = CORPUS_DAYS - 1 - spike.startsDaysAgo;
  return { from, to: from + spike.days - 1 };
}

/** Which spikes are open on a given day index, if any. */
function spikesOn(dayIndex: number): readonly Spike[] {
  return spikes.filter((spike) => {
    const { from, to } = spikeWindow(spike);
    return dayIndex >= from && dayIndex <= to;
  });
}

/**
 * The engagement mix, as relative weights. Likes dominate, shares are scarce
 * and worth the most — the ordering `ENGAGEMENT_TYPES` encodes.
 */
const ENGAGEMENT_MIX: Readonly<Record<EngagementType, number>> = {
  shares: 5,
  comments: 15,
  reactions: 19,
  likes: 61,
};

/**
 * Per-channel multipliers on that mix, so a by-type chart split by channel
 * shows four different shapes rather than the same proportions four times.
 * Same conditional philosophy as the dimension tables above. A channel absent
 * here takes the base mix.
 */
const ENGAGEMENT_TILT: Readonly<Record<string, Partial<Record<EngagementType, number>>>> = {
  TikTok: { shares: 2.6, likes: 1.1, comments: 0.7 },
  X: { shares: 2.2, comments: 1.3, reactions: 0.5 },
  Reddit: { comments: 3.2, likes: 0.4, shares: 0.6 },
  YouTube: { comments: 2.1, shares: 0.8 },
  LinkedIn: { reactions: 1.6, comments: 1.2, likes: 0.7 },
  Instagram: { likes: 1.3, comments: 0.8 },
};

/** Probability a mention on this channel was placed rather than organic. */
const SEEDED_BY_CHANNEL: Readonly<Record<string, number>> = {
  Facebook: 0.34,
  Instagram: 0.31,
  YouTube: 0.22,
  TikTok: 0.26,
  X: 0.14,
  LinkedIn: 0.18,
  Reddit: 0.05,
};

/** Fallback for a channel with no entry above. */
const SEEDED_DEFAULT = 0.2;

/* ------------------------------------------------------------------ *
 * Media outlets. See `MentionRow.outlet`.
 * ------------------------------------------------------------------ */

/**
 * The only conversation types a media organisation can produce.
 *
 * A newspaper publishes; it does not send a direct message, file a support
 * ticket, open a live chat or leave an app-store review. So outlet attribution
 * is gated on the shape of the conversation rather than sprayed across the
 * corpus at some overall rate — which is the difference between "13% of
 * mentions are press" and "press mentions look like press".
 *
 * `mention` is the editorial one (the outlet names the brand in a story);
 * `quote-post` and `repost` are the amplification shapes, which for a brand
 * story are very often a masthead's account carrying it.
 *
 * **Measured on the default 70-day window, scoped to Grameenphone** (the Media
 * module's own slice, since `Type=Default` draws no Brands chip): 409 rows, of
 * which these three hold 100 — `mention` 61, `repost` 21, `quote-post` 18. That
 * is the ceiling on the module's volume and the reason its trend card buckets
 * weekly rather than daily; see `MediaView`.
 */
const PRESS_CONVERSATIONS: readonly string[] = ['mention', 'quote-post', 'repost'];

/**
 * Probability an eligible mention was published by an outlet rather than by an
 * ordinary account, by channel.
 *
 * High where mastheads actually publish and get quoted (Facebook, X), low where
 * the format is not news (TikTok, Reddit — where a brand story arrives via a
 * user, not via the paper's own account).
 *
 * These are deliberately high. The honest alternative — a realistic ~40% — puts
 * the module at roughly 40 stories per 70 days across eight outlets, which is
 * one bar of five rows and a trend nobody can read. Attributing most of an
 * already-narrow, already-broadcast-shaped slice is the cheaper distortion, and
 * it is confined to this table rather than smeared over the eligibility rule.
 */
const OUTLET_BY_CHANNEL: Readonly<Record<string, number>> = {
  facebook: 0.92,
  x: 0.95,
  youtube: 0.78,
  linkedin: 0.85,
  instagram: 0.6,
  tiktok: 0.35,
  reddit: 0.15,
};

/** Fallback for a channel with no entry above. */
const OUTLET_CHANNEL_DEFAULT = 0.7;

/** Base share of coverage per outlet. `The Daily Star` leads — see `OUTLET_VALUES`. */
const OUTLET_MIX: Weights = {
  'the-daily-star': 26,
  'prothom-alo': 19,
  bdnews24: 15,
  'the-business-standard': 12,
  'dhaka-tribune': 10,
  'somoy-tv': 8,
  'jamuna-tv': 6,
  'new-age': 4,
};

/**
 * outlet | sentiment — which mastheads carry the critical stories.
 *
 * Conditioned this way round, rather than sentiment on outlet, because
 * `sentiment` is drawn earlier in the chain and reversing that would mean
 * re-ordering the existing draws and rewriting the corpus. Reading it forwards:
 * a negative story is likelier to be in `New Age` or `The Business Standard`
 * than on `Somoy TV`.
 *
 * Without this table every bar in "Top media outlets by sentiment" shows the
 * same proportions eight times over, which is the flat-fixture look
 * `ENGAGEMENT_TILT` exists to avoid on its own chart.
 */
const OUTLET_BY_SENTIMENT: Readonly<Record<string, Weights>> = {
  negative: {
    'new-age': 2.2,
    'the-business-standard': 1.5,
    'dhaka-tribune': 1.3,
    'somoy-tv': 0.6,
    'jamuna-tv': 0.7,
  },
  positive: {
    'somoy-tv': 1.7,
    'jamuna-tv': 1.5,
    'prothom-alo': 1.2,
    'new-age': 0.4,
    'the-business-standard': 0.8,
  },
};

/**
 * Which outlet published a mention, or `null` when no organisation did.
 *
 * Returns before drawing anything for an ineligible row, so the stream advances
 * only on rows that could carry an outlet. Still fully deterministic: both the
 * conversation and the sentiment it branches on are already drawn by the time
 * this is called, so the sequence of draws is fixed by the seed.
 */
function drawOutlet(
  rand: () => number,
  conversation: string,
  channel: string,
  sentiment: string,
): string | null {
  if (!PRESS_CONVERSATIONS.includes(conversation)) return null;

  /* Eligible, but still possibly an ordinary account on this channel. */
  if (rand() >= (OUTLET_BY_CHANNEL[channel] ?? OUTLET_CHANNEL_DEFAULT)) return null;

  const overlay = OUTLET_BY_SENTIMENT[sentiment];
  const weights = OUTLET_VALUES.map((outlet) => {
    const base = OUTLET_MIX[outlet] ?? 1;
    return overlay && outlet in overlay ? base * overlay[outlet] : base;
  });

  return OUTLET_VALUES[pickWeighted(rand, weights)];
}

/* ------------------------------------------------------------------ *
 * Keywords and hashtags — the two vocabularies the Trends module reads.
 * Rosters at the top of the file.
 * ------------------------------------------------------------------ */

/**
 * topic | keyword — which word a mention on each topic actually uses.
 *
 * Conditioned this way round because `topic` is drawn well before this and
 * reversing it would mean re-ordering every existing draw. Reading it forwards:
 * a mention filed under `network-coverage` says `নেটওয়ার্ক` most of the time and
 * `টাওয়ার` or `5G` some of it, which is what stops the keyword cloud from being
 * the topic cloud with different spelling.
 *
 * Every one of the twenty topics has an entry. A topic falling through to the
 * flat default would draw uniformly across all fourteen keywords, which reads
 * as noise rather than as vocabulary — and the load-time check below fails on a
 * missing entry rather than letting that happen quietly.
 */
const KEYWORD_BY_TOPIC: Readonly<Record<string, Weights>> = {
  'network-coverage': { network: 40, 'no-network': 16, signal: 12, tower: 10, 'five-g': 8, 'four-g': 8, internet: 6 },
  'call-quality': { 'call-drop': 30, network: 22, signal: 14, tower: 10, minutes: 8, complaint: 6 },
  'internet-speed': { internet: 30, speed: 20, 'slow-internet': 16, 'four-g': 10, 'five-g': 8, data: 8, network: 6 },
  'data-packs': { data: 28, package: 18, gb: 14, validity: 12, offer: 8, internet: 8, price: 6 },
  pricing: { price: 24, package: 18, expensive: 16, offer: 12, discount: 10, bill: 8, gb: 6 },
  billing: { bill: 30, deduction: 18, balance: 14, refund: 10, complaint: 8, recharge: 6 },
  recharge: { recharge: 34, flexiload: 18, bkash: 14, balance: 12, 'emergency-balance': 10, cashback: 8 },
  promotions: { offer: 34, bonus: 16, discount: 14, cashback: 12, package: 10, mygp: 6 },
  'device-offers': { handset: 28, offer: 20, discount: 12, 'five-g': 10, 'four-g': 8, package: 6 },
  'loyalty-rewards': { star: 30, points: 22, offer: 14, bonus: 12, mygp: 10, discount: 6 },
  'app-experience': { mygp: 34, 'app-crash': 18, login: 14, update: 12, recharge: 8, balance: 6 },
  'customer-service': { 'customer-care': 34, helpline: 20, complaint: 16, center: 10, refund: 8, sim: 6 },
  'store-experience': { center: 30, sim: 20, 'sim-replacement': 16, 'customer-care': 12, nid: 8, esim: 6 },
  'sim-activation': { sim: 30, nid: 18, biometric: 16, esim: 12, 'sim-replacement': 10, 'customer-care': 6 },
  roaming: { roaming: 36, 'international-call': 22, data: 12, bill: 10, package: 8, sms: 6 },
  'contract-terms': { bill: 22, validity: 18, package: 16, deduction: 12, 'customer-care': 10, sms: 6 },
  advertising: { ad: 30, offer: 20, mygp: 12, star: 10, 'five-g': 8, package: 6 },
  sponsorships: { ad: 22, star: 18, offer: 14, network: 12, 'five-g': 10, mygp: 8 },
  sustainability: { tower: 28, network: 18, 'five-g': 14, signal: 12, 'customer-care': 8, ad: 6 },
  careers: { job: 34, 'customer-care': 16, center: 12, mygp: 10, network: 8, ad: 6 },
};

/**
 * Weight a keyword carries when its topic's table does not name it.
 *
 * Low, not zero. Zero would mean a topic could *never* produce a word its table
 * forgot, which turns a fixture omission into a hard claim about the data;
 * a small floor lets the long tail exist while keeping the named words dominant.
 *
 * **0.25, down from 1.5 when the roster grew from 14 to 50 (2026-09-18).** The
 * floor applies to every unnamed keyword, so its total grows with the roster:
 * at 1.5 the 44 unnamed words per topic would carry 66 weight against the named
 * ~100, and the tail would be two-fifths of the corpus. At 0.25 it is 11, about
 * a tenth, which is where the fourteen-word roster had it. That share also keeps
 * `RELATED_SHARE` in `data/keywords.ts` meaningful: a floor-only keyword sits
 * near 0.2% of a topic, a named one at 5% or more.
 */
const KEYWORD_FLOOR = 0.25;

/**
 * Which keyword the pipeline matched.
 *
 * Reads `topic` only. Sentiment deliberately does **not** overlay this: the
 * `Keyword mentions and sentiment` card asks how each keyword's tone splits, and
 * conditioning the keyword on the sentiment as well would build the answer into
 * the fixture instead of letting it fall out of `SENTIMENT_BY_TOPIC`. The tonal
 * differences between keywords on that card are inherited from their topics,
 * which is the honest route to them.
 */
function drawKeyword(rand: () => number, topic: string): string {
  const overlay = KEYWORD_BY_TOPIC[topic];
  const weights = KEYWORD_VALUES.map((keyword) =>
    overlay && keyword in overlay ? overlay[keyword] : KEYWORD_FLOOR,
  );
  return KEYWORD_VALUES[pickWeighted(rand, weights)];
}

/**
 * The conversation shapes a hashtag can appear on.
 *
 * A tag is a public-post affordance: you tag a post, a story, a repost or a
 * quote so strangers can find it. You do not tag a direct message, a support
 * ticket, a live chat or an app-store review, because none of them has an
 * audience to be found by. `comment` and `reply` are in because a tagged reply
 * under a campaign post is ordinary; `review` and `support-ticket` are out for
 * the same reason `PRESS_CONVERSATIONS` excludes them.
 *
 * Measured on the default 70-day window scoped to Grameenphone: see the note in
 * `docs/rules.md` — this is the same eligibility-then-rate shape `drawOutlet`
 * uses, and for the same reason.
 */
const HASHTAG_CONVERSATIONS: readonly string[] = [
  'mention',
  'quote-post',
  'repost',
  'comment',
  'reply',
  'story-mention',
  'forum-thread',
  'video-comment',
];

/**
 * Probability an eligible mention carried a tag at all, by channel.
 *
 * Follows the platforms' own conventions rather than one flat rate: Instagram
 * and TikTok posts are tagged as a matter of course, Facebook and X sometimes,
 * LinkedIn rarely and Reddit essentially never — a subreddit *is* the tag.
 */
const HASHTAG_BY_CHANNEL: Readonly<Record<string, number>> = {
  instagram: 0.82,
  tiktok: 0.78,
  x: 0.55,
  facebook: 0.42,
  youtube: 0.38,
  linkedin: 0.22,
  reddit: 0.04,
};

/** Fallback for a channel with no entry above. */
const HASHTAG_CHANNEL_DEFAULT = 0.4;

/**
 * topic | hashtag. Coarser than `KEYWORD_BY_TOPIC` on purpose — a tag is a
 * campaign or a category, not a word, so several topics share one.
 */
const HASHTAG_BY_TOPIC: Readonly<Record<string, Weights>> = {
  'network-coverage': { network: 46, 'five-g': 18, services: 12, mobile: 10 },
  'call-quality': { network: 42, services: 18, mobile: 12, community: 8 },
  'internet-speed': { data: 34, network: 24, 'five-g': 16, mobile: 10 },
  'data-packs': { data: 40, packages: 28, offer: 12, mobile: 8 },
  pricing: { packages: 34, offer: 24, services: 14, data: 10 },
  billing: { services: 34, packages: 18, app: 14, mygp: 12 },
  recharge: { mygp: 30, app: 22, services: 16, packages: 12 },
  promotions: { offer: 46, packages: 18, data: 12, mygp: 10 },
  'device-offers': { offer: 38, mobile: 22, packages: 14, 'five-g': 10 },
  'loyalty-rewards': { offer: 32, mygp: 24, community: 14, app: 10 },
  'app-experience': { app: 44, mygp: 30, services: 10, data: 6 },
  'customer-service': { services: 44, community: 16, app: 12, mygp: 10 },
  'store-experience': { services: 36, community: 18, mobile: 14, mygp: 10 },
  'sim-activation': { services: 34, mobile: 24, mygp: 14, app: 10 },
  roaming: { services: 30, data: 22, mobile: 16, packages: 10 },
  'contract-terms': { services: 30, packages: 24, mobile: 12, app: 10 },
  advertising: { offer: 28, mygp: 20, community: 16, mobile: 12 },
  sponsorships: { community: 34, mygp: 20, offer: 16, 'five-g': 10 },
  sustainability: { community: 30, network: 20, 'five-g': 16, services: 10 },
  careers: { community: 34, mygp: 18, services: 14, mobile: 10 },
};

/** `KEYWORD_FLOOR`'s reason, on the other roster. */
const HASHTAG_FLOOR = 1.5;

/**
 * Which hashtag the mention carried, or `null` when it carried none.
 *
 * Returns before drawing anything for an ineligible row, exactly as
 * `drawOutlet` does, so the stream advances only on rows that could carry a tag
 * and the corpus stays reproducible from the seed alone.
 */
function drawHashtag(
  rand: () => number,
  conversation: string,
  channel: string,
  topic: string,
): string | null {
  if (!HASHTAG_CONVERSATIONS.includes(conversation)) return null;

  if (rand() >= (HASHTAG_BY_CHANNEL[channel] ?? HASHTAG_CHANNEL_DEFAULT)) return null;

  const overlay = HASHTAG_BY_TOPIC[topic];
  const weights = HASHTAG_VALUES.map((hashtag) =>
    overlay && hashtag in overlay ? overlay[hashtag] : HASHTAG_FLOOR,
  );
  return HASHTAG_VALUES[pickWeighted(rand, weights)];
}

/* ------------------------------------------------------------------ *
 * Community, individual voices, audience and geography.
 * The rosters these draw from are declared at the top of the file.
 * ------------------------------------------------------------------ */

/**
 * Relative volume by local hour, midnight first.
 *
 * Overnight trough, mid-morning rise, a dip over lunch, an early-evening peak —
 * the shape the DeepDive v4 prototype drew its heat grid from, reused here as
 * **draw weights** rather than as cell values. The difference matters: the
 * prototype painted the curve directly onto the grid, while here it decides
 * when a mention happens and the grid is then summed from the mentions that
 * landed. A filter therefore moves the heatmap, which a painted curve could not
 * do.
 *
 * Relative, not absolute: `pickWeighted` normalises, so these are read against
 * each other and the day's own volume still comes from `lambda`.
 */
const HOUR_SHAPE: readonly number[] = [
  90, 62, 41, 28, 24, 31, 68, 142, 238, 286, 264, 231, 248, 286, 312, 348, 402, 496, 624, 712, 648,
  512, 348, 186,
];

/**
 * The conversation shapes that can happen inside a community.
 *
 * Gated on the shape rather than sprayed at a rate, exactly as
 * `PRESS_CONVERSATIONS` is: a group carries threads, posts and the replies to
 * them. It does not carry a direct message, a support ticket, a live chat or an
 * app-store review — those are private or first-party surfaces, and stamping a
 * group name on one would claim a private complaint was a public thread.
 *
 * **Measured on the default 70-day window scoped to Grameenphone:** 409 rows,
 * of which these four hold 208 — `comment` 86, `mention` 61, `reply` 51,
 * `forum-thread` 10.
 */
const COMMUNITY_CONVERSATIONS: readonly string[] = ['forum-thread', 'mention', 'comment', 'reply'];

/**
 * Which of those are a **post** rather than a response to one.
 *
 * Exported because the activity timeline is built on this split and the two
 * series have to mean what this table says they mean. Modelling posts and
 * comments as two conversation shapes — rather than as a row count against the
 * `comments` engagement field, which is what the prototype did — is what keeps
 * them the same *kind* of number. A count of posts (about one a day) stacked
 * under a sum of comment engagement (about ninety a day) draws one visible band
 * and one invisible one.
 */
export const COMMUNITY_POST_CONVERSATIONS: readonly string[] = ['forum-thread', 'mention'];

/**
 * Probability an eligible mention happened inside a monitored community rather
 * than in the open, by channel.
 *
 * Highest where groups and subreddits *are* the format (Facebook, Reddit),
 * lowest where the surface is a feed with no group layer (TikTok, Instagram).
 *
 * **Deliberately high, for `OUTLET_BY_CHANNEL`'s reason.** A realistic rate —
 * most Facebook comments are on brand pages, not in groups — leaves roughly 60
 * community rows per 70 days spread over five communities and two series on the
 * timeline, which is a chart of single digits. Attributing most of an
 * already-group-shaped slice is the cheaper distortion, and confining it to
 * this table keeps the eligibility rule above honest.
 */
const COMMUNITY_BY_CHANNEL: Readonly<Record<string, number>> = {
  facebook: 0.95,
  reddit: 0.95,
  linkedin: 0.6,
  youtube: 0.6,
  x: 0.55,
  instagram: 0.5,
  tiktok: 0.45,
};

/** Fallback for a channel with no entry above. */
const COMMUNITY_CHANNEL_DEFAULT = 0.6;

/**
 * Base share of community conversation, in `COMMUNITY_VALUES` order. Taken from
 * the prototype's own totals (12.4K / 6.8K / 4.9K / 3.6K / 2.9K), normalised.
 */
const COMMUNITY_MIX: Weights = {
  'gp-users-bd': 30,
  'bd-telecom-forum': 17,
  'dhaka-tech-talk': 12,
  'mobile-deals-bd': 9,
  'student-sim-offers': 7,
};

/**
 * community | sentiment — which groups have turned hostile.
 *
 * Conditioned this way round for `OUTLET_BY_SENTIMENT`'s reason: `sentiment` is
 * drawn earlier in the chain, and reversing that would mean re-ordering the
 * existing draws and rewriting the corpus.
 *
 * Each weight is the prototype's own tone mix for that community divided by the
 * corpus's own, so the table says "twice as negative as the average
 * conversation" rather than an invented constant. Measured base, default window
 * scoped to Grameenphone: positive 41.6%, neutral 36.2%, negative 22.2%.
 * Neutral is absent because every ratio came out within a hair of the same
 * number — it is flat across the five, so an entry would say nothing.
 *
 * Without this table all five bars on "Top communities by sentiment" show the
 * same proportions five times over.
 */
const COMMUNITY_BY_SENTIMENT: Readonly<Record<string, Weights>> = {
  negative: {
    'gp-users-bd': 0.55,
    'bd-telecom-forum': 1.45,
    'dhaka-tech-talk': 2.1,
    'mobile-deals-bd': 0.95,
    'student-sim-offers': 0.36,
  },
  positive: {
    'gp-users-bd': 1.6,
    'bd-telecom-forum': 1.05,
    'dhaka-tech-talk': 0.78,
    'mobile-deals-bd': 1.4,
    'student-sim-offers': 1.8,
  },
};

/**
 * The conversation shapes a public account can author.
 *
 * The exclusions are the point: a direct message, a support ticket, a live chat,
 * a product review and an app-store review are not things an account with an
 * audience does *in public*, and a voice ranked by reach has to be reachable.
 */
const VOICE_CONVERSATIONS: readonly string[] = [
  'mention',
  'quote-post',
  'repost',
  'comment',
  'reply',
  'video-comment',
  'forum-thread',
  'blog-comment',
  'story-mention',
];

/**
 * Probability an eligible mention came from one of the named voices, by channel.
 *
 * An order of magnitude below the community rates, because this is the opposite
 * kind of claim: a community is a place many people post in, a voice is six
 * specific accounts among thousands. Highest where a following is the currency
 * (X, YouTube, TikTok), lowest where accounts are pseudonymous (Reddit).
 */
const VOICE_BY_CHANNEL: Readonly<Record<string, number>> = {
  x: 0.46,
  youtube: 0.42,
  tiktok: 0.4,
  instagram: 0.34,
  facebook: 0.3,
  linkedin: 0.26,
  reddit: 0.12,
};

/** Fallback for a channel with no entry above. */
const VOICE_CHANNEL_DEFAULT = 0.3;

/** Base share of voice activity, in `VOICE_VALUES` order. */
const VOICE_MIX: Weights = {
  gpcarehelp: 22,
  techbdreview: 20,
  dhakadeals: 18,
  simoffersbd: 16,
  rifatnetworks: 13,
  banglatechtalk: 11,
};

/**
 * voice | sentiment — which accounts are advocates and which are detractors.
 *
 * The stance a card prints is **derived from the mix this produces**, never
 * stored: an account is an advocate because most of what it posted was
 * positive, which is a reading of the period rather than a label the fixture
 * asserts. Deselect the positive sentiments and an advocate stops being one,
 * which is the honest behaviour.
 */
const VOICE_BY_SENTIMENT: Readonly<Record<string, Weights>> = {
  negative: {
    techbdreview: 2.6,
    rifatnetworks: 2.4,
    banglatechtalk: 2.2,
    gpcarehelp: 0.3,
    dhakadeals: 0.4,
    simoffersbd: 0.45,
  },
  positive: {
    gpcarehelp: 2.2,
    dhakadeals: 1.9,
    simoffersbd: 1.8,
    techbdreview: 0.3,
    rifatnetworks: 0.35,
    banglatechtalk: 0.4,
  },
};

/**
 * The audience mix — share of mentions by gender and age band, the bands in
 * `AGE_BANDS` order. The ten weights are read as one joint distribution rather
 * than two independent draws, because age and gender are correlated in it: the
 * male skew is widest at 25-34 and narrowest at 18-24.
 *
 * Straight from the prototype's own pyramid, which sums to 100.
 */
const AUDIENCE_MIX: Readonly<Record<string, readonly number[]>> = {
  male: [14, 21, 12, 7, 4],
  female: [12, 14, 9, 5, 2],
};

/**
 * audience | sentiment — the complaint skew inside the audience.
 *
 * Each weight is the prototype's negative-mention pyramid divided by its
 * all-mention pyramid, so the table is derived from the two figures the
 * prototype drew rather than invented beside them: negatives skew young, and
 * the oldest band is close to absent from them.
 *
 * `55+ female` is 0.15 rather than the 0.0 the division gives, because a zero
 * weight removes the band from the draw entirely and a pyramid missing one
 * corner reads as a rendering fault rather than as a small number.
 *
 * The card's whole argument rests on this table. The aggregate pyramid barely
 * moves in a fortnight; the complaint skew inside it is the thing worth a card.
 */
const AUDIENCE_BY_SENTIMENT: Readonly<Record<string, Readonly<Record<string, readonly number[]>>>> =
  {
    negative: {
      male: [1.36, 1.1, 0.92, 0.57, 0.25],
      female: [1.17, 1.0, 0.78, 0.4, 0.15],
    },
  };

/**
 * The intents that count as a complaint.
 *
 * Wider than the `complaint` chip alone, and the width is the point: a billing
 * issue, an outage report, a refund request and a cancellation are all somebody
 * telling us something is wrong. Measured on the default window scoped to
 * Grameenphone, the family holds 128 rows against `complaint`'s own 62 — which
 * is the difference between ~21 and ~10 per division, and a per-100K rate over
 * ten rows reshuffles its own ranking every time a chip moves.
 *
 * Exported because the card that reads it must use the same definition the
 * corpus was skewed with, or the ranking it draws is not the one that was
 * generated.
 */
export const COMPLAINT_INTENTS: readonly string[] = [
  'complaint',
  'billing-issue',
  'outage-report',
  'refund-request',
  'cancellation',
];

/**
 * Where complaints concentrate, over and above where the subscribers are.
 *
 * The base draw is proportional to `DIVISION_SUBSCRIBERS_K`, so without this
 * table every division files complaints at the same rate per head and the card
 * ranks nothing — it would redraw the subscriber base six times over.
 *
 * The weights are the prototype's own complaints-per-100K divided by its
 * corpus-wide average of 18.9: Sylhet at 36.9 is 1.95× the average, Rangpur at
 * 10.5 is 0.56×. Applied to the complaint family only, so ordinary
 * conversation still follows the population.
 */
const DIVISION_COMPLAINT_TILT: Weights = {
  sylhet: 1.95,
  dhaka: 1.1,
  chattogram: 0.92,
  khulna: 0.84,
  rajshahi: 0.66,
  rangpur: 0.56,
};

/** Which local hour a mention was posted in. Every row gets one. */
function drawHour(rand: () => number): number {
  return pickWeighted(rand, HOUR_SHAPE as number[]);
}

/**
 * Which community a mention was posted in, or `null` when it was posted in the
 * open.
 *
 * Returns before drawing anything for an ineligible row, so the stream advances
 * only on rows that could carry a community — the same discipline `drawOutlet`
 * keeps, and the reason both stay deterministic: every field they branch on is
 * already drawn by the time they are called.
 */
function drawCommunity(
  rand: () => number,
  conversation: string,
  channel: string,
  sentiment: string,
): string | null {
  if (!COMMUNITY_CONVERSATIONS.includes(conversation)) return null;
  if (rand() >= (COMMUNITY_BY_CHANNEL[channel] ?? COMMUNITY_CHANNEL_DEFAULT)) return null;

  const overlay = COMMUNITY_BY_SENTIMENT[sentiment];
  const weights = COMMUNITY_VALUES.map((community) => {
    const base = COMMUNITY_MIX[community] ?? 1;
    return overlay && community in overlay ? base * overlay[community] : base;
  });

  return COMMUNITY_VALUES[pickWeighted(rand, weights)];
}

/**
 * Which named voice posted a mention, or `null` — which is most of them.
 *
 * `outlet` is checked first and wins: a row already attributed to a masthead
 * cannot also be an individual with a following, or the same reach would be
 * counted once as earned media and once as a personal voice. That is why this
 * is drawn after `outlet` rather than beside it.
 */
function drawVoice(
  rand: () => number,
  outlet: string | null,
  conversation: string,
  channel: string,
  sentiment: string,
): string | null {
  if (outlet !== null) return null;
  if (!VOICE_CONVERSATIONS.includes(conversation)) return null;
  if (rand() >= (VOICE_BY_CHANNEL[channel] ?? VOICE_CHANNEL_DEFAULT)) return null;

  const overlay = VOICE_BY_SENTIMENT[sentiment];
  const weights = VOICE_VALUES.map((voice) => {
    const base = VOICE_MIX[voice] ?? 1;
    return overlay && voice in overlay ? base * overlay[voice] : base;
  });

  return VOICE_VALUES[pickWeighted(rand, weights)];
}

/**
 * The author's age band and gender, drawn together off the joint table.
 *
 * One draw over ten cells rather than two draws over five and two: drawing them
 * independently would flatten the correlation `AUDIENCE_MIX` carries and make
 * every band show the same male share, which is exactly the shape the pyramid
 * exists to disprove.
 */
function drawAudience(rand: () => number, sentiment: string): { ageBand: string; gender: string } {
  const overlay = AUDIENCE_BY_SENTIMENT[sentiment];

  const cells: { ageBand: string; gender: string; weight: number }[] = [];
  for (const gender of GENDER_VALUES) {
    AGE_BANDS.forEach((ageBand, i) => {
      const base = AUDIENCE_MIX[gender][i];
      cells.push({ ageBand, gender, weight: base * (overlay?.[gender][i] ?? 1) });
    });
  }

  const cell = cells[pickWeighted(rand, cells.map((c) => c.weight))];
  return { ageBand: cell.ageBand, gender: cell.gender };
}

/**
 * Which division a mention came from — the subscriber base, tilted for the
 * complaint family. See `DIVISION_COMPLAINT_TILT`.
 */
function drawDivision(rand: () => number, intent: string): string {
  const complaining = COMPLAINT_INTENTS.includes(intent);

  const weights = DIVISION_VALUES.map((division) => {
    const base = DIVISION_SUBSCRIBERS_K[division];
    return complaining ? base * (DIVISION_COMPLAINT_TILT[division] ?? 1) : base;
  });

  return DIVISION_VALUES[pickWeighted(rand, weights)];
}


/**
 * The conversation shapes a tracked creator's activity can take.
 *
 * Ten of the fourteen, and what is *refused* is the shorter list: a
 * `direct-message`, a `support-ticket` and a `live-chat` are private exchanges
 * with the operator rather than published work, and an `app-store-review` is
 * left with the ordinary customers who write them. Stamping any of the four on
 * a creator would claim a helpdesk conversation was a post.
 *
 * `comment` and `reply` are here for two reasons. A creator talking *back* to
 * their audience is a real part of what this module measures — and the
 * `Industry specific split` table (964:16136) asks for `Total comments` and
 * `Total replies` as columns beside `Total posts`, which no engagement column
 * in this corpus can answer. Reading all three as conversation shapes makes
 * them row counts over disjoint sets, so they neither overlap nor need a field
 * inventing for them. See `INFLUENCER_POST_CONVERSATIONS` below for the split.
 */
const INFLUENCER_CONVERSATIONS: readonly string[] = [
  'mention',
  'quote-post',
  'repost',
  'story-mention',
  'review',
  'forum-thread',
  'blog-comment',
  'video-comment',
  'comment',
  'reply',
];

/**
 * The subset of the above that is a *post* rather than a response to one —
 * `Total posts` on both detail tables, and the denominator of every per-post
 * figure the module prints.
 */
export const INFLUENCER_POST_CONVERSATIONS: readonly string[] = [
  'mention',
  'quote-post',
  'repost',
  'story-mention',
  'review',
  'forum-thread',
];

/**
 * `Total comments` on the industry table.
 *
 * Two shapes, not one: a `video-comment` is a comment, and leaving it out would
 * put the column an order of magnitude below the one beside it on exactly the
 * channels — YouTube, TikTok — where creators are busiest.
 */
export const INFLUENCER_COMMENT_CONVERSATIONS: readonly string[] = ['comment', 'video-comment', 'blog-comment'];

/** `Total replies` on the industry table. */
export const INFLUENCER_REPLY_CONVERSATIONS: readonly string[] = ['reply'];

/**
 * Probability an eligible row belongs to a tracked creator, by channel.
 *
 * Highest where creators actually are — TikTok and Instagram are the format —
 * and near zero on Reddit and LinkedIn, where a brand mention comes from an
 * ordinary account or a company page rather than from someone with a following.
 *
 * Deliberately high overall, for `OUTLET_BY_CHANNEL`'s reason: a realistic rate
 * would leave thirty creators sharing a few hundred rows, which is a donut of
 * two slices and an industry table of ones and zeros.
 */
const INFLUENCER_BY_CHANNEL: Readonly<Record<string, number>> = {
  tiktok: 0.92,
  instagram: 0.9,
  facebook: 0.72,
  /*
   * Listed at zero rather than omitted. No creator in the roster is on any of
   * these, so `drawInfluencer` returns before the rate is read and the value is
   * inert today — it is here to say that the absence is deliberate, and to give
   * the next person adding a YouTube creator the one line they have to change.
   */
  youtube: 0,
  x: 0,
  linkedin: 0,
  reddit: 0,
};

const INFLUENCER_CHANNEL_DEFAULT = 0.5;

/*
 * **A per-conversation multiplier on that rate was tried and removed
 * (2026-09-17), and the measurement is the reason.** The corpus's conversation
 * mix leans towards `comment` and `reply`, so a creator's activity is mostly
 * responses and `Posts analyzed` is the smallest figure on the list screen.
 * Tilting attribution towards post shapes looks like the fix and is not: the
 * post-shaped rows on Facebook, Instagram and TikTok are *already* nearly all
 * attributed at the channel rates above, so there are no more posts to find.
 * Weighting comments down therefore bought 71 posts where the flat rate gives
 * 74, while halving the other two columns — 56 comments against 119 and 19
 * replies against 46 — and leaving two creators with no posts at all.
 *
 * The post count is capacity-limited, not mix-limited. Raising it means more
 * post-shaped rows in the corpus, which is a change to `CONVERSATION_BY_CHANNEL`
 * on the main stream and would move every module already built.
 */


/**
 * How much more likely a creator is to post in their own industry than in any
 * other.
 *
 * `2.2`, which lands the primary at ~35% and the other four at ~16% each. Set
 * against the frame's own split (2,480 / 1,965 / 1,742 / 1,386 / 1,128 — a
 * 30/24/21/17/14 ladder), not against a guess: a heavier tilt gives one slice
 * half the ring and four slivers, which is not the shape the design draws.
 */
const INDUSTRY_PRIMARY_TILT = 2.2;

/**
 * Which creator posted a mention, or `null`.
 *
 * Gated three ways before a weight table is even consulted, because each gate
 * refuses a different kind of wrong attribution: a masthead's story is not a
 * creator's post, a row already attributed to a named `voice` cannot be a
 * second named person, and a private conversation is not creator activity.
 *
 * Weighted by the square root of following rather than by following itself.
 * Linear weights put Ayesha Rahman (1.28M) 111× above Jubair Alam (11.5K) and
 * the roster collapses to its top three; the square root gives ~11×, which is a
 * steep but readable ranking — and it is the same "weight the ranking, do not
 * engineer the window" rule `OUTLET_MIX` follows.
 */
function drawInfluencer(
  rand: () => number,
  channel: string,
  conversation: string,
  outlet: string | null,
  voice: string | null,
): string | null {
  if (outlet !== null || voice !== null) return null;
  if (!INFLUENCER_CONVERSATIONS.includes(conversation)) return null;

  /*
   * Before the rate draw, not after. No creator is on LinkedIn, Reddit, X or
   * YouTube, so a row on one of those can never carry one however the coin
   * lands — and taking the draw anyway would advance this stream on half the
   * corpus for nothing, making every later attribution depend on rows that were
   * never candidates.
   */
  const candidates = INFLUENCERS.filter((profile) => profile.platforms.includes(channel));
  if (candidates.length === 0) return null;

  const rate = INFLUENCER_BY_CHANNEL[channel] ?? INFLUENCER_CHANNEL_DEFAULT;
  if (rand() >= rate) return null;

  const weights = candidates.map((profile) => Math.sqrt(profile.followers));
  return candidates[pickWeighted(rand, weights)].id;
}

/**
 * Which industry a creator's post falls in — their own, tilted, or any other.
 *
 * Takes the profile rather than the id so the caller's lookup is not repeated,
 * and so a creator who somehow is not in the roster cannot silently draw a
 * uniform industry.
 */
function drawIndustry(rand: () => number, profile: InfluencerProfile): string {
  const weights = INDUSTRY_VALUES.map((industry) =>
    industry === profile.industry ? INDUSTRY_PRIMARY_TILT : 1,
  );
  return INDUSTRY_VALUES[pickWeighted(rand, weights)];
}

/**
 * How safe one post is, drawn from the creator's own standing score.
 *
 * `P(safe)` *is* the score, so a creator scored 80 posts `safe` 80% of the
 * time and the ring on the details page agrees with the figure in its middle
 * without either being computed from the other. What is left over splits two
 * to one between `needs-review` and `risky`: the middle band is the common
 * case, and a post nobody would want to stand next to is rarer than one that
 * merely wants a human to look at it.
 */
function drawSafetyBand(rand: () => number, profile: InfluencerProfile): string {
  const safe = Math.max(0, Math.min(100, profile.safety)) / 100;
  const rest = 1 - safe;
  return SAFETY_BANDS[pickWeighted(rand, [safe, rest * (2 / 3), rest * (1 / 3)])];
}

/**
 * Split a total into four parts that sum back to it **exactly**.
 *
 * Largest-remainder: take the floor of each share, then hand the leftover units
 * one at a time to whichever parts lost the most in rounding. Rounding each
 * share independently would drift by up to three counts per row, and across
 * four thousand rows that is a visible disagreement between a stacked by-type
 * chart and the engagement figure printed above it.
 */
function splitEngagement(
  rand: () => number,
  total: number,
  channel: string,
): Record<EngagementType, number> {
  const tilt = ENGAGEMENT_TILT[channel] ?? {};

  /* Jittered per row so the mix is a tendency, not a constant. */
  const weights = ENGAGEMENT_TYPES.map(
    (type) => ENGAGEMENT_MIX[type] * (tilt[type] ?? 1) * (0.75 + rand() * 0.5),
  );
  const sum = weights.reduce((a, b) => a + b, 0);

  const exact = weights.map((w) => (w / sum) * total);
  const floors = exact.map(Math.floor);
  let remainder = total - floors.reduce((a, b) => a + b, 0);

  const order = exact
    .map((value, i) => ({ i, frac: value - Math.floor(value) }))
    .sort((a, b) => b.frac - a.frac);

  const out = [...floors];
  for (let k = 0; remainder > 0; k += 1, remainder -= 1) {
    out[order[k % order.length].i] += 1;
  }

  return Object.fromEntries(ENGAGEMENT_TYPES.map((type, i) => [type, out[i]])) as Record<
    EngagementType,
    number
  >;
}

/**
 * Build the whole corpus.
 *
 * One `rand` is threaded through the entire pass — see `mulberry32` for why
 * per-row reseeding would band the output.
 *
 * **A second stream, `extra`, draws the fields added in 2026-09** (`origin` and
 * the engagement split). It exists so those fields cost `rand` nothing: a
 * mulberry32 is a sequence, so taking two more draws per row inside the main
 * stream would shift every subsequent value and silently rewrite the entire
 * corpus — every KPI, every chart shape, and every figure recorded in the Phase
 * 2–4 write-ups. Threading a separate stream leaves the original byte-identical.
 *
 * This is not the per-row reseeding `mulberry32` warns about: `extra` is one
 * stream threaded through the whole pass, exactly as `rand` is. Its seed is
 * mixed with the golden-ratio constant so the two sequences do not march in
 * step.
 *
 * **A third stream, `press`, draws `outlet`** (2026-09-17), for exactly the
 * reason `extra` exists — and it has to be a third rather than more draws on
 * `extra`, which is the trap here. `extra` is already consumed five times per
 * row (once for `origin`, four times inside `splitEngagement`); taking a sixth
 * would shift every subsequent row's `origin` and engagement split, rewriting
 * the fields the Phase 5 and 6 write-ups measured.
 *
 * **A fourth stream, `crowd`, draws the Community fields** (2026-09-17):
 * `hour`, `division`, `ageBand`, `gender`, `community` and `voice`. Same trap,
 * one step further out — `press` is consumed once per *eligible* row, so an
 * extra draw on it would shift which outlet published every later story. Six
 * fields on one new stream rather than six new streams, because they are one
 * generation: they are added together, so no ordering among them can be
 * disturbed by a later one.
 *
 * Four streams, four generations of field, each leaving the ones before it
 * untouched. Proven the way the rule asks: SHA-256 over the 16 pre-existing
 * fields across all 4,573 rows, identical before and after
 * (`417517bf1f6237568f04c3944a3a96b52f50ac285c75ebe881513b86d2ff44fb`).
 */
export function generateRows(seed: number): MentionRow[] {
  const rand = mulberry32(seed);
  const extra = mulberry32((seed ^ 0x9e3779b9) >>> 0);
  const press = mulberry32((seed ^ 0x85eb_ca6b) >>> 0);
  const crowd = mulberry32((seed ^ 0xc2b2_ae35) >>> 0);
  /**
   * A **fifth independent stream**, added with the Trends module, and it is
   * independent for a specific reason: the draws below are interleaved into a
   * loop that already ran, so taking them from `rand`, `extra`, `press` or
   * `crowd` would advance those sequences by two per row and silently change
   * every figure in every other module. A new stream leaves all four untouched
   * — verified by diffing the corpus before and after.
   */
  const vocab = mulberry32((seed ^ 0x27d4_eb2f) >>> 0);
  /**
   * A **sixth independent stream**, added with the Influencers module, for the
   * reason `vocab` is a fifth: its draws interleave into a loop that already
   * ran five times over, so taking them from any existing stream would advance
   * that sequence by up to three per row and move every figure in every module
   * built before this one. Verified the way the rule asks — SHA-256 over the 24
   * pre-existing fields across all 4,573 rows, identical before and after
   * (`8617cd2486bc3419432375e188989c73a27321b886f9085dc463511c7257db7d`).
   */
  const creators = mulberry32((seed ^ 0x1656_67b1) >>> 0);
  const rows: MentionRow[] = [];

  for (let dayIndex = 0; dayIndex < CORPUS_DAYS; dayIndex += 1) {
    const date = addDays(CORPUS_START, dayIndex);
    const day = date.getTime();
    const open = spikesOn(dayIndex);

    /* lambda(d) = base × (1 + drift·d) × weekday[dow] × spikeMultiplier(d) */
    let lambda = BASE_LAMBDA * (1 + (DRIFT * dayIndex) / CORPUS_DAYS) * WEEKDAY[date.getDay()];
    for (const spike of open) lambda *= spike.multiplier;

    const count = poisson(rand, lambda);

    for (let i = 0; i < count; i += 1) {
      /*
       * Drawn conditionally, in this order, so each dimension can lean on the
       * one before it:
       *   brand → channel|brand → intent|channel → topic|intent
       *         → conversation|channel → sentiment|(brand, topic, spike)
       */
      const brand = draw(rand, 'brand', [bias(open, 'brand')]);
      const channel = draw(rand, 'channel', [CHANNEL_BY_BRAND[brand], bias(open, 'channel')]);
      const intent = draw(rand, 'intent', [INTENT_BY_CHANNEL[channel], bias(open, 'intent')]);
      const topic = draw(rand, 'topic', [TOPIC_BY_INTENT[intent], bias(open, 'topic')]);
      const conversation = draw(rand, 'conversation', [
        CONVERSATION_BY_CHANNEL[channel],
        bias(open, 'conversation'),
      ]);
      const sentiment = draw(rand, 'sentiment', [
        SENTIMENT_BY_TOPIC[topic],
        SENTIMENT_BY_BRAND[brand],
        bias(open, 'sentiment'),
      ]);

      /*
       * Long-tailed, not uniform: most mentions reach very few people and a
       * handful reach a great many. A uniform draw would make every bar the
       * same height, which is the "dummy data that looks fake" this avoids.
       */
      const reach = logNormal(rand, 1800, 1.15);
      const engagement = Math.min(reach, logNormal(rand, Math.max(2, reach * 0.04), 0.8));

      const [mean, deviation] = NPS_BY_SENTIMENT[sentiment];
      const npsScore = Math.max(0, Math.min(10, Math.round(gaussian(rand, mean, deviation))));

      const origin: Origin =
        extra() < (SEEDED_BY_CHANNEL[channel] ?? SEEDED_DEFAULT) ? 'seeded' : 'organic';
      const byType = splitEngagement(extra, engagement, channel);
      const outlet = drawOutlet(press, conversation, channel, sentiment);

      /*
       * The `crowd` generation, in a fixed order. `voice` reads `outlet`, which
       * is why it is last — a row already attributed to a masthead cannot also
       * be an individual with a following.
       */
      const hour = drawHour(crowd);
      const division = drawDivision(crowd, intent);
      const { ageBand, gender } = drawAudience(crowd, sentiment);
      const community = drawCommunity(crowd, conversation, channel, sentiment);
      const voice = drawVoice(crowd, outlet, conversation, channel, sentiment);

      /*
       * The `vocab` generation. `keyword` is unconditional and `hashtag` gated,
       * so the stream advances once or twice per row — which is fine, because
       * nothing else reads it.
       */
      const keyword = drawKeyword(vocab, topic);
      const hashtag = drawHashtag(vocab, conversation, channel, topic);

      /*
       * The `creators` generation, in a fixed order and gated as one: a row
       * with no creator draws neither an industry nor a safety band, so the
       * stream advances once on most rows and three times on a creator's. That
       * is the same shape `vocab` already has and is fine for the same reason —
       * nothing else reads this stream.
       *
       * `influencer` reads `outlet` and `voice` and must therefore come after
       * both, the way `voice` itself comes after `outlet`.
       */
      const influencer = drawInfluencer(creators, channel, conversation, outlet, voice);
      const profile = influencer === null ? undefined : INFLUENCER_BY_ID[influencer];
      const industry = profile ? drawIndustry(creators, profile) : null;
      const safetyBand = profile ? drawSafetyBand(creators, profile) : null;

      rows.push({
        day,
        brand,
        channel,
        intent,
        topic,
        conversation,
        sentiment,
        engagement,
        reach,
        npsScore,
        origin,
        outlet,
        ...byType,
        hour,
        community,
        voice,
        keyword,
        hashtag,
        ageBand,
        gender,
        division,
        influencer,
        industry,
        safetyBand,
      });
    }
  }

  return rows;
}

/** Merge the open spikes' bias for one dimension into a single overlay. */
function bias(open: readonly Spike[], key: DimensionKey): Weights | undefined {
  if (open.length === 0) return undefined;
  if (open.length === 1) return open[0].bias[key];
  const merged: Record<string, number> = {};
  for (const spike of open) {
    for (const [value, weight] of Object.entries(spike.bias[key] ?? {})) {
      merged[value] = (merged[value] ?? 1) * weight;
    }
  }
  return merged;
}

/**
 * Fails at module load if any weight table names a value the chips do not offer.
 *
 * A typo like `'network coverage'` for `'network-coverage'` is otherwise
 * invisible: the overlay silently does nothing and the outage spike loses its
 * topic bias, leaving a bump with no story. Cheap to check, impossible to spot
 * by eye.
 */
export function assertWeightTablesMatchFixtures(): void {
  const check = (key: DimensionKey, table: Weights, where: string) => {
    for (const value of Object.keys(table)) {
      if (!dimensionValues[key].includes(value)) {
        throw new Error(
          `generator: ${where} names ${key} "${value}", which the chips do not offer. ` +
            `Allowed: ${dimensionValues[key].join(', ')}.`,
        );
      }
    }
  };

  for (const key of dimensionKeys) check(key, BASE[key], `BASE.${key}`);
  for (const [brand, table] of Object.entries(CHANNEL_BY_BRAND)) {
    check('brand', { [brand]: 1 }, 'CHANNEL_BY_BRAND key');
    check('channel', table, `CHANNEL_BY_BRAND.${brand}`);
  }
  for (const [channel, table] of Object.entries(INTENT_BY_CHANNEL)) {
    check('channel', { [channel]: 1 }, 'INTENT_BY_CHANNEL key');
    check('intent', table, `INTENT_BY_CHANNEL.${channel}`);
  }
  for (const [intent, table] of Object.entries(TOPIC_BY_INTENT)) {
    check('intent', { [intent]: 1 }, 'TOPIC_BY_INTENT key');
    check('topic', table, `TOPIC_BY_INTENT.${intent}`);
  }
  for (const [channel, table] of Object.entries(CONVERSATION_BY_CHANNEL)) {
    check('channel', { [channel]: 1 }, 'CONVERSATION_BY_CHANNEL key');
    check('conversation', table, `CONVERSATION_BY_CHANNEL.${channel}`);
  }
  for (const [topic, table] of Object.entries(SENTIMENT_BY_TOPIC)) {
    check('topic', { [topic]: 1 }, 'SENTIMENT_BY_TOPIC key');
    check('sentiment', table, `SENTIMENT_BY_TOPIC.${topic}`);
  }
  for (const [brand, table] of Object.entries(SENTIMENT_BY_BRAND)) {
    check('brand', { [brand]: 1 }, 'SENTIMENT_BY_BRAND key');
    check('sentiment', table, `SENTIMENT_BY_BRAND.${brand}`);
  }
  check('sentiment', NPS_BY_SENTIMENT as unknown as Weights, 'NPS_BY_SENTIMENT');

  /*
   * The outlet tables, checked against the same two closed sets they name. A
   * typo here is the `'network coverage'` bug again with a quieter symptom: a
   * misspelled conversation silently empties `PRESS_CONVERSATIONS` of one of its
   * three members and the Media module loses a fifth of its corpus, while a
   * misspelled outlet makes an overlay do nothing and every bar on the card come
   * out the same shape.
   */
  check('conversation', Object.fromEntries(PRESS_CONVERSATIONS.map((c) => [c, 1])), 'PRESS_CONVERSATIONS');
  check('channel', OUTLET_BY_CHANNEL, 'OUTLET_BY_CHANNEL');

  /**
   * The same check against a **roster** — a closed set with no chip behind it.
   * `dimensionValues` cannot serve these: outlets, communities, voices and
   * divisions are declared in this file, not in the filter fixtures.
   */
  const roster = (values: readonly string[], noun: string) => (table: Weights, where: string) => {
    for (const value of Object.keys(table)) {
      if (!values.includes(value)) {
        throw new Error(
          `generator: ${where} names ${noun} "${value}", which the roster does not list. ` +
            `Allowed: ${values.join(', ')}.`,
        );
      }
    }
  };

  /** Every roster value has a display name, so no chart can draw a bare slug. */
  const labelled = (values: readonly string[], labels: Readonly<Record<string, string>>, where: string) => {
    for (const value of values) {
      if (!(value in labels)) throw new Error(`generator: ${where} has no entry for "${value}".`);
    }
  };

  const checkOutlets = roster(OUTLET_VALUES, 'outlet');
  checkOutlets(OUTLET_MIX, 'OUTLET_MIX');
  for (const [sentiment, table] of Object.entries(OUTLET_BY_SENTIMENT)) {
    check('sentiment', { [sentiment]: 1 }, 'OUTLET_BY_SENTIMENT key');
    checkOutlets(table, `OUTLET_BY_SENTIMENT.${sentiment}`);
  }
  labelled(OUTLET_VALUES, OUTLET_LABELS, 'OUTLET_LABELS');

  /*
   * The Community tables, checked the same way and for the same reason. The
   * quiet failure here is the worse one: a misspelled conversation drops a
   * quarter of the module's corpus, and a misspelled community makes an overlay
   * do nothing so all five bars on "Top communities by sentiment" come out the
   * same shape — a chart that looks built rather than broken.
   */
  check('conversation', Object.fromEntries(COMMUNITY_CONVERSATIONS.map((c) => [c, 1])), 'COMMUNITY_CONVERSATIONS');
  check('conversation', Object.fromEntries(COMMUNITY_POST_CONVERSATIONS.map((c) => [c, 1])), 'COMMUNITY_POST_CONVERSATIONS');
  check('conversation', Object.fromEntries(VOICE_CONVERSATIONS.map((c) => [c, 1])), 'VOICE_CONVERSATIONS');
  check('intent', Object.fromEntries(COMPLAINT_INTENTS.map((i) => [i, 1])), 'COMPLAINT_INTENTS');
  check('channel', COMMUNITY_BY_CHANNEL, 'COMMUNITY_BY_CHANNEL');
  check('channel', VOICE_BY_CHANNEL, 'VOICE_BY_CHANNEL');

  /* A post shape that is not also a community shape would never be drawn. */
  for (const conversation of COMMUNITY_POST_CONVERSATIONS) {
    if (!COMMUNITY_CONVERSATIONS.includes(conversation)) {
      throw new Error(
        `generator: COMMUNITY_POST_CONVERSATIONS names "${conversation}", which is not in ` +
          `COMMUNITY_CONVERSATIONS — no row could ever carry it, so the timeline's posts series ` +
          `would be silently empty.`,
      );
    }
  }

  const checkCommunities = roster(COMMUNITY_VALUES, 'community');
  checkCommunities(COMMUNITY_MIX, 'COMMUNITY_MIX');
  for (const [sentiment, table] of Object.entries(COMMUNITY_BY_SENTIMENT)) {
    check('sentiment', { [sentiment]: 1 }, 'COMMUNITY_BY_SENTIMENT key');
    checkCommunities(table, `COMMUNITY_BY_SENTIMENT.${sentiment}`);
  }
  checkCommunities(COMMUNITY_MEMBERS, 'COMMUNITY_MEMBERS');
  checkCommunities(Object.fromEntries([...OWNED_COMMUNITIES].map((c) => [c, 1])), 'OWNED_COMMUNITIES');
  labelled(COMMUNITY_VALUES, COMMUNITY_LABELS, 'COMMUNITY_LABELS');

  const checkVoices = roster(VOICE_VALUES, 'voice');
  checkVoices(VOICE_MIX, 'VOICE_MIX');
  for (const [sentiment, table] of Object.entries(VOICE_BY_SENTIMENT)) {
    check('sentiment', { [sentiment]: 1 }, 'VOICE_BY_SENTIMENT key');
    checkVoices(table, `VOICE_BY_SENTIMENT.${sentiment}`);
  }
  labelled(VOICE_VALUES, VOICE_LABELS, 'VOICE_LABELS');

  /*
   * The Influencers tables. The quiet failure here is a creator whose
   * `platforms` list names a channel the chips do not offer: `drawInfluencer`
   * filters the roster by exact channel match, so that creator is simply never
   * drawn — they appear in the list table with a follower count and zero posts,
   * which reads as a data gap rather than as a typo.
   */
  check('conversation', Object.fromEntries(INFLUENCER_CONVERSATIONS.map((c) => [c, 1])), 'INFLUENCER_CONVERSATIONS');
  check('channel', INFLUENCER_BY_CHANNEL, 'INFLUENCER_BY_CHANNEL');
  for (const group of [
    ['INFLUENCER_POST_CONVERSATIONS', INFLUENCER_POST_CONVERSATIONS],
    ['INFLUENCER_COMMENT_CONVERSATIONS', INFLUENCER_COMMENT_CONVERSATIONS],
    ['INFLUENCER_REPLY_CONVERSATIONS', INFLUENCER_REPLY_CONVERSATIONS],
  ] as const) {
    for (const conversation of group[1]) {
      if (!INFLUENCER_CONVERSATIONS.includes(conversation)) {
        throw new Error(
          `generator: ${group[0]} names "${conversation}", which is not in ` +
            `INFLUENCER_CONVERSATIONS — no row could ever carry it, so that column would be ` +
            `silently zero on every industry.`,
        );
      }
    }
  }

  /* The three column sets must not overlap, or `Total posts` would double-count. */
  const seenConversation = new Set<string>();
  for (const conversation of [
    ...INFLUENCER_POST_CONVERSATIONS,
    ...INFLUENCER_COMMENT_CONVERSATIONS,
    ...INFLUENCER_REPLY_CONVERSATIONS,
  ]) {
    if (seenConversation.has(conversation)) {
      throw new Error(
        `generator: "${conversation}" is in more than one of the industry table's column sets, ` +
          `so its rows would be counted twice.`,
      );
    }
    seenConversation.add(conversation);
  }

  for (const profile of INFLUENCERS) {
    for (const channel of profile.platforms) {
      check('channel', { [channel]: 1 }, `INFLUENCERS.${profile.id}.platforms`);
    }
    if (profile.platforms.length === 0) {
      throw new Error(`generator: influencer "${profile.id}" is on no channel, so no row can reach them.`);
    }
    if (!INDUSTRY_VALUES.includes(profile.industry)) {
      throw new Error(
        `generator: influencer "${profile.id}" names industry "${profile.industry}", which the ` +
          `roster does not list. Allowed: ${INDUSTRY_VALUES.join(', ')}.`,
      );
    }
    if (!Number.isFinite(profile.safety) || profile.safety < 0 || profile.safety > 100) {
      throw new Error(
        `generator: influencer "${profile.id}" has brand safety ${profile.safety}, outside 0–100 — ` +
          `it is drawn against as a probability, so the bands would be nonsense.`,
      );
    }
  }
  if (new Set(INFLUENCER_VALUES).size !== INFLUENCER_VALUES.length) {
    throw new Error('generator: INFLUENCERS has a duplicate id, so two creators would share a route.');
  }
  labelled(INDUSTRY_VALUES, INDUSTRY_LABELS, 'INDUSTRY_LABELS');
  labelled(SAFETY_BANDS, SAFETY_BAND_LABELS, 'SAFETY_BAND_LABELS');
  labelled(INFLUENCER_VALUES, INFLUENCER_LABELS, 'INFLUENCER_LABELS');

  const checkDivisions = roster(DIVISION_VALUES, 'division');
  checkDivisions(DIVISION_COMPLAINT_TILT, 'DIVISION_COMPLAINT_TILT');
  checkDivisions(DIVISION_SUBSCRIBERS_K, 'DIVISION_SUBSCRIBERS_K');
  labelled(DIVISION_VALUES, DIVISION_LABELS, 'DIVISION_LABELS');
  for (const division of DIVISION_VALUES) {
    if (!(division in DIVISION_SUBSCRIBERS_K)) {
      throw new Error(
        `generator: DIVISION_SUBSCRIBERS_K has no entry for "${division}" — it is the draw's own ` +
          `base weight, so the division would never be drawn at all.`,
      );
    }
  }

  /*
   * The audience table is positional rather than keyed, so the check is a length
   * one: a sixth age band added to `AGE_BANDS` without a sixth weight would draw
   * `undefined` and take every row with it.
   */
  for (const gender of GENDER_VALUES) {
    if (AUDIENCE_MIX[gender]?.length !== AGE_BANDS.length) {
      throw new Error(
        `generator: AUDIENCE_MIX.${gender} has ${AUDIENCE_MIX[gender]?.length ?? 0} weights for ` +
          `${AGE_BANDS.length} age bands.`,
      );
    }
    for (const [sentiment, table] of Object.entries(AUDIENCE_BY_SENTIMENT)) {
      check('sentiment', { [sentiment]: 1 }, 'AUDIENCE_BY_SENTIMENT key');
      if (table[gender]?.length !== AGE_BANDS.length) {
        throw new Error(
          `generator: AUDIENCE_BY_SENTIMENT.${sentiment}.${gender} has ` +
            `${table[gender]?.length ?? 0} weights for ${AGE_BANDS.length} age bands.`,
        );
      }
    }
  }
  labelled(GENDER_VALUES, GENDER_LABELS, 'GENDER_LABELS');

  /*
   * The keyword and hashtag tables. The failure these catch is the loudest of
   * the lot: `KEYWORD_BY_TOPIC` is keyed on every topic, so a typo does not
   * merely weaken an overlay — it drops that topic through to a flat floor and
   * the keyword cloud for it becomes fourteen words of equal size, which is the
   * one thing a cloud must never be.
   */
  const checkKeywords = roster(KEYWORD_VALUES, 'keyword');
  for (const [topic, table] of Object.entries(KEYWORD_BY_TOPIC)) {
    check('topic', { [topic]: 1 }, 'KEYWORD_BY_TOPIC key');
    checkKeywords(table, `KEYWORD_BY_TOPIC.${topic}`);
  }
  labelled(KEYWORD_VALUES, KEYWORD_LABELS, 'KEYWORD_LABELS');

  /*
   * Every keyword is named by at least one topic. A keyword only the floor
   * reaches is near 0.2% of any topic, so it would never rank, never be found
   * as related, and look like a roster bug on every chart.
   */
  const named = new Set(Object.values(KEYWORD_BY_TOPIC).flatMap((table) => Object.keys(table)));
  for (const keyword of KEYWORD_VALUES) {
    if (!named.has(keyword)) {
      throw new Error(`generator: keyword "${keyword}" is named by no topic in KEYWORD_BY_TOPIC.`);
    }
  }

  /* Labels are looked up in reverse (label → gloss), so they must be unique. */
  const seenLabels = new Set<string>();
  for (const keyword of KEYWORD_VALUES) {
    const label = KEYWORD_LABELS[keyword];
    if (seenLabels.has(label)) throw new Error(`generator: KEYWORD_LABELS repeats "${label}".`);
    seenLabels.add(label);
  }
  /* `null` is a legitimate gloss — "already English" — so the key must exist
     even where the value does not. `labelled` cannot express that. */
  for (const keyword of KEYWORD_VALUES) {
    if (!(keyword in KEYWORD_GLOSS)) {
      throw new Error(`generator: KEYWORD_GLOSS has no entry for "${keyword}".`);
    }
  }

  /*
   * Every topic needs its own entry, which is stricter than the outlet tables
   * are — those are overlays on a base mix and a missing key just means "no
   * tilt". This table *is* the distribution, so a missing key is a flat one.
   */
  for (const topic of dimensionValues.topic) {
    if (!(topic in KEYWORD_BY_TOPIC)) {
      throw new Error(
        `generator: KEYWORD_BY_TOPIC has no entry for topic "${topic}" — every keyword would be ` +
          `equally likely on it, and the cloud would draw fourteen words the same size.`,
      );
    }
    if (!(topic in HASHTAG_BY_TOPIC)) {
      throw new Error(`generator: HASHTAG_BY_TOPIC has no entry for topic "${topic}".`);
    }
  }

  check(
    'conversation',
    Object.fromEntries(HASHTAG_CONVERSATIONS.map((c) => [c, 1])),
    'HASHTAG_CONVERSATIONS',
  );
  check('channel', HASHTAG_BY_CHANNEL, 'HASHTAG_BY_CHANNEL');

  const checkHashtags = roster(HASHTAG_VALUES, 'hashtag');
  for (const [topic, table] of Object.entries(HASHTAG_BY_TOPIC)) {
    check('topic', { [topic]: 1 }, 'HASHTAG_BY_TOPIC key');
    checkHashtags(table, `HASHTAG_BY_TOPIC.${topic}`);
  }
  labelled(HASHTAG_VALUES, HASHTAG_LABELS, 'HASHTAG_LABELS');

  /* 24 hours, 24 weights — the one table indexed by the clock. */
  if (HOUR_SHAPE.length !== 24) {
    throw new Error(`generator: HOUR_SHAPE has ${HOUR_SHAPE.length} weights, not 24.`);
  }

  for (const spike of spikes) {
    for (const key of dimensionKeys) {
      const table = spike.bias[key];
      if (table) check(key, table, `spike "${spike.id}".bias.${key}`);
    }
  }
}

/** Each spike's window as real dates — for the debug panel and for reading the charts. */
export function spikeWindows(): { spike: Spike; from: Date; to: Date }[] {
  return spikes.map((spike) => {
    const { from, to } = spikeWindow(spike);
    return { spike, from: addDays(CORPUS_START, from), to: addDays(CORPUS_START, to) };
  });
}
